import type {AuditAdjustHeader, AuditAdjustLine} from '@/services/auditService/interface/adjust-orchestrate';
import {
    getOriginalEntryFromFsCategory,
    resolveFsNodeOriginalEntry,
} from '@/adjustment/adjust/dialogs/utils/adjustment-helpers';

type AdjustmentEntry = { adjustment?: number | null; adjustHeaderId?: string; headerId?: string };

const LOAN_DETAIL_ARRAY_KEYS = ['bankLoans', 'vehicleLoans'] as const;

function sumAdjustmentEntries(entries: AdjustmentEntry[] | null | undefined, headerId?: string): number | undefined {
    if (!Array.isArray(entries) || entries.length === 0) return undefined;

    const matchHeader = (entry: AdjustmentEntry) => {
        if (headerId == null) return true;
        const hid = entry.adjustHeaderId ?? entry.headerId;
        return hid == null || String(hid) === String(headerId);
    };

    const matched = entries.filter(matchHeader);
    const source = matched.length > 0 ? matched : entries;
    const total = source.reduce((sum, entry) => sum + (Number(entry?.adjustment) || 0), 0);
    return total;
}

/**
 * PL-AE-REN only: sum rentalAdj/otherAdj for the current header.
 * Unlike sumAdjustmentEntries, never falls back to all entries when none match —
 * rows often carry multiple headers' attrs and fallback would over-count (111+60→171).
 */
function sumRentalAdjustmentEntriesStrict(
    entries: AdjustmentEntry[] | null | undefined,
    headerId?: string,
): number | undefined {
    if (!Array.isArray(entries) || entries.length === 0) return undefined;

    const matchHeader = (entry: AdjustmentEntry) => {
        if (headerId == null) return true;
        const hid = entry.adjustHeaderId ?? entry.headerId;
        return hid == null || String(hid) === String(headerId);
    };

    const matched = entries.filter(matchHeader);
    if (matched.length === 0) return undefined;

    return matched.reduce((sum, entry) => sum + (Number(entry?.adjustment) || 0), 0);
}

/** Loan WP field adj VO: { currAdj, nonCurrAdj, inAdj } (not a flat AdjustmentDetail[]). */
export function isLoanFieldAdjustmentsObject(adj: unknown): boolean {
    if (!adj || typeof adj !== 'object' || Array.isArray(adj)) return false;
    const o = adj as Record<string, unknown>;
    return 'currAdj' in o || 'nonCurrAdj' in o || 'inAdj' in o;
}

export function isLoanWpDetail(detail: unknown): boolean {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const d = detail as Record<string, unknown>;
    return Array.isArray(d.bankLoans) || Array.isArray(d.vehicleLoans);
}

/** Loan field WP→WP FS codes (Current / Non-current / Interest). */
export const LOAN_FIELD_FS_CODES = new Set([
    'BS-CL-BLS',
    'BS-NCL-BLS',
    'BS-CL-OFL',
    'BS-NCL-OFL',
    'PL-FC-LI',
]);

export function isLoanFieldFsCode(code?: string | null): boolean {
    return LOAN_FIELD_FS_CODES.has(String(code || '').trim().toUpperCase());
}

/** PL-FC-LI Interest Summary WP shape — only when code is PL-FC-LI (or omitted for structural peek). */
export function isInterestSummaryDetail(detail: unknown, code?: string | null): boolean {
    if (code != null && String(code).trim().toUpperCase() !== 'PL-FC-LI') return false;
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const d = detail as Record<string, unknown>;
    return d.interestSummary != null && typeof d.interestSummary === 'object' && !Array.isArray(d.interestSummary);
}

/**
 * PL-FC-LI adjustment execution shape for WP→WP submit.
 * interestSummary is display-only; attrs / inAdj ownership requires bankLoans | vehicleLoans + adjustment.
 */
export function createPlFcLiAdjustmentDetail(
    adjustmentAmount: number,
    options?: {wpFetchCode?: string | null; perClient?: number},
): Record<string, any> {
    const perClient = Number(options?.perClient) || 0;
    const fetch = String(options?.wpFetchCode || '').trim().toUpperCase();
    const loanRow = {id: 1, perClient, adjustment: 0, lastYearFinal: 0};
    if (fetch === 'BS-CL-OFL' || fetch === 'BS-NCL-OFL') {
        return {vehicleLoans: [loanRow], adjustment: adjustmentAmount};
    }
    return {bankLoans: [loanRow], adjustment: adjustmentAmount};
}

/**
 * Normalize PL-FC-LI adjustment details to { bankLoans|vehicleLoans, adjustment }.
 * Keeps existing loan arrays when present; converts interestSummary (display) to loan field shape.
 * Scoped to PL-FC-LI only — other loan field codes keep their existing submit paths.
 */
export function ensurePlFcLiLoanFieldAdjustmentDetail(
    detail: any,
    adjustmentAmount: number,
    options?: {wpFetchCode?: string | null; perClient?: number},
): Record<string, any> {
    const passed = Number(adjustmentAmount);
    const existing = Number(detail?.adjustment);
    const adj = Number.isFinite(passed) && Math.abs(passed) >= 0.001
        ? passed
        : (Number.isFinite(existing) ? existing : 0);
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const hasBank = Array.isArray(detail.bankLoans) && detail.bankLoans.length > 0;
        const hasVehicle = Array.isArray(detail.vehicleLoans) && detail.vehicleLoans.length > 0;
        if (hasBank || hasVehicle) {
            const {interestSummary: _displayOnly, ...rest} = detail as Record<string, any>;
            return {...rest, adjustment: adj};
        }
    }
    return createPlFcLiAdjustmentDetail(adj, options);
}

/**
 * AdjustTabsDialog line-level `_synthetic` edit targets for loan field codes only.
 * Requires `code` so other WPs are never matched by structural keys alone.
 */
export function isLoanFieldSyntheticDetail(detail: unknown, code?: string | null): boolean {
    if (!isLoanFieldFsCode(code)) return false;
    return isLoanWpDetail(detail) || isInterestSummaryDetail(detail, code);
}

/** PL-AE-REN field WP→WP FS code. */
export function isRentalFieldFsCode(code?: string | null): boolean {
    return String(code || '').trim().toUpperCase() === 'PL-AE-REN';
}

/** PL-AE-REN field WP→WP shape: non-empty `rows` (sibling of top-level `adjustment`). */
export function isRentalFieldWpDetail(detail: unknown): boolean {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const rows = (detail as Record<string, unknown>).rows;
    return Array.isArray(rows) && rows.length > 0;
}

/**
 * AdjustTabsDialog `_synthetic` edit target for PL-AE-REN field WP→WP.
 * Requires `code` so other WPs with a `rows` key are never matched.
 */
export function isRentalFieldSyntheticDetail(detail: unknown, code?: string | null): boolean {
    return isRentalFieldFsCode(code) && isRentalFieldWpDetail(detail);
}

/** PPE field WP→WP FS codes (Cost / Depn). */
export const PPE_FIELD_FS_CODES = new Set([
    'BS-NCA-PPE',
    'PL-AE-DEP',
]);

export const PPE_ROW_ARRAY_KEYS = [
    'locationOfPropertyRow',
    'commercialAllowanceRow',
    'industrialAllowanceRow',
    'furnitureFixtureRow',
    'computerHardSoftRow',
    'fullPayMotorRow',
    'hirePurchaseMotorRow',
    'machineryRow',
    'liftEscalatorRow',
    'equipmentRow',
] as const;

/** Top-level submit keys ↔ nested attr list keys on row.adjustments. */
const PPE_FIELD_LIST_MAP = [
    {listKey: 'costAdjs', fieldKey: 'costAdj'},
    {listKey: 'addAdjs', fieldKey: 'addAdj'},
    {listKey: 'nbvCfAdjs', fieldKey: 'nbvCfAdj'},
    // Legacy Acc dep b/f — keep for AdjustTabs consolidate of existing headers (UI no longer creates)
    {listKey: 'accDepAdjs', fieldKey: 'accDepAdj'},
    {listKey: 'depnChgAdjs', fieldKey: 'depnChgAdj'},
] as const;

export const PPE_FIELD_ADJ_KEYS = ['costAdj', 'addAdj', 'nbvCfAdj', 'accDepAdj', 'depnChgAdj'] as const;

const PPE_BS_OWNED_FIELD_KEYS: ReadonlySet<string> = new Set(['costAdj', 'addAdj', 'nbvCfAdj', 'accDepAdj']);
const PPE_PL_OWNED_FIELD_KEYS: ReadonlySet<string> = new Set(['depnChgAdj']);

/**
 * FS-code ownership for nested PPE field adj lists (mirrors PropertySubTable FROM codes).
 * Only applied when the same WP detail nests both BS-owned and PL-owned lists
 * (cost/add/nbvCf/accDep + depnChg) — the GET shape after cost/additions auto PL-AE-DEP companion.
 * All other PPE field-adj paths keep the legacy full-list sum.
 */
function ppeFieldListMapForCode(code?: string | null) {
    const upper = String(code || '').trim().toUpperCase();
    if (upper === 'PL-AE-DEP') {
        return PPE_FIELD_LIST_MAP.filter((m) => m.fieldKey === 'depnChgAdj');
    }
    if (upper === 'BS-NCA-PPE') {
        return PPE_FIELD_LIST_MAP.filter((m) => m.fieldKey !== 'depnChgAdj');
    }
    return PPE_FIELD_LIST_MAP;
}

/** True when nested/top-level field adjs mix BS-owned keys and depnChg (cross-line inflation risk). */
function detailHasCrossOwnedPpeFieldAdjs(
    detail: Record<string, any> | null | undefined,
): boolean {
    if (!detail || typeof detail !== 'object') return false;
    let hasBsOwned = false;
    let hasDepn = false;

    const markKey = (fieldKey: string) => {
        if (PPE_BS_OWNED_FIELD_KEYS.has(fieldKey)) hasBsOwned = true;
        if (PPE_PL_OWNED_FIELD_KEYS.has(fieldKey)) hasDepn = true;
    };

    for (const k of PPE_FIELD_ADJ_KEYS) {
        const v = Number(detail[k]);
        if (Number.isFinite(v) && Math.abs(v) >= 0.001) markKey(k);
    }

    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = detail[rowKey];
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            const adj = (row as any).adjustments;
            if (!isPpeFieldAdjustmentsObject(adj)) continue;
            for (const {listKey, fieldKey} of PPE_FIELD_LIST_MAP) {
                const list = (adj as Record<string, unknown>)[listKey];
                if (Array.isArray(list) && list.length > 0) markKey(fieldKey);
            }
            if (hasBsOwned && hasDepn) return true;
        }
    }
    return hasBsOwned && hasDepn;
}

/**
 * Apply FS-owned list filter only for BS-NCA-PPE / PL-AE-DEP when cross-owned
 * field adjs are present. Otherwise return full map (legacy behavior).
 */
function resolvePpeFieldListMapForCollect(
    code?: string | null,
    detail?: Record<string, any> | null,
) {
    const upper = String(code || '').trim().toUpperCase();
    if (upper !== 'BS-NCA-PPE' && upper !== 'PL-AE-DEP') {
        return PPE_FIELD_LIST_MAP;
    }
    if (!detailHasCrossOwnedPpeFieldAdjs(detail)) {
        return PPE_FIELD_LIST_MAP;
    }
    return ppeFieldListMapForCode(code);
}

function shouldStripCrossOwnedPpeFieldKeys(
    code?: string | null,
    detail?: Record<string, any> | null,
): boolean {
    const upper = String(code || '').trim().toUpperCase();
    if (upper !== 'BS-NCA-PPE' && upper !== 'PL-AE-DEP') return false;
    return detailHasCrossOwnedPpeFieldAdjs(detail);
}

export function isPpeFieldFsCode(code?: string | null): boolean {
    return PPE_FIELD_FS_CODES.has(String(code || '').trim().toUpperCase());
}

/** PPE field adj VO: { costAdjs, addAdjs, nbvCfAdjs, depnChgAdjs (+ legacy accDepAdjs) }. */
export function isPpeFieldAdjustmentsObject(adj: unknown): boolean {
    if (!adj || typeof adj !== 'object' || Array.isArray(adj)) return false;
    const o = adj as Record<string, unknown>;
    // IA-only VO ({amorChgAdjs} and/or nbvCfAdjs without PPE list keys) must not match PPE
    if ('amorChgAdjs' in o) return false;
    if ('nbvCfAdjs' in o
        && !('costAdjs' in o)
        && !('addAdjs' in o)
        && !('depnChgAdjs' in o)
        && !('accDepAdjs' in o)) {
        return false;
    }
    return 'costAdjs' in o || 'addAdjs' in o || 'nbvCfAdjs' in o || 'accDepAdjs' in o || 'depnChgAdjs' in o;
}

// ─── Intangible field WP→WP (BS-NCA-IA / PL-AE-AIA) ─────────────────────────

// ─── Intangible field WP→WP (BS-NCA-IA / PL-AE-AIA / BS-NCA-ITL) ───────────
// PL-AE-DEP is NOT in this set — it is shared with PPE. Taxi Depn uses PL-AE-DEP
// only when detail/attrs carry iaField:* / amorChgAdj (see isItlDepIaFieldCode).

const IA_FIELD_FS_CODES = new Set([
    'BS-NCA-IA',
    'PL-AE-AIA',
    'BS-NCA-ITL',
    // CommonInvestment NBV C/F toPaper / field adj（与 IA nbvCfAdj 同形）
    'BS-NCA-ISUB',
    'BS-NCA-IASS',
    'BS-CA-IOT',
]);
const IA_ROW_ARRAY_KEY = 'intangibleRows';
const PL_AE_DEP_CODE = 'PL-AE-DEP';

export const IA_FIELD_ADJ_KEYS = ['amorChgAdj', 'nbvCfAdj'] as const;

const IA_FIELD_LIST_MAP = [
    {listKey: 'amorChgAdjs', fieldKey: 'amorChgAdj'},
    {listKey: 'nbvCfAdjs', fieldKey: 'nbvCfAdj'},
] as const;

export function isIaFieldFsCode(code?: string | null): boolean {
    return IA_FIELD_FS_CODES.has(String(code || '').trim().toUpperCase());
}

/**
 * PL-AE-DEP as Taxi Depn field-adj line (iaField:amorChgAdj).
 * Must not match PPE's ppeField:depnChgAdj on the same FS code.
 */
export function isItlDepIaFieldCode(code?: string | null, detail?: unknown): boolean {
    const upper = String(code || '').trim().toUpperCase();
    if (upper !== PL_AE_DEP_CODE) return false;
    if (detail == null) return false;
    if (String((detail as any)?.rowClass || '').toLowerCase().startsWith('iafield:')) {
        return true;
    }
    return isIaFieldAdjActiveDetail(detail);
}

/** IA/AIA/ITL codes, or DEP only when Taxi iaField evidence is present. */
export function isIaStyleFieldFsCode(code?: string | null, detail?: unknown): boolean {
    return isIaFieldFsCode(code) || isItlDepIaFieldCode(code, detail);
}

/** Owned field key: Amor/Depn → AIA|DEP(taxi); NBV C/F → IA|ITL. */
export function iaOwnedFieldKey(code?: string | null): (typeof IA_FIELD_ADJ_KEYS)[number] {
    const upper = String(code || '').trim().toUpperCase();
    return upper === 'PL-AE-AIA' || upper === PL_AE_DEP_CODE ? 'amorChgAdj' : 'nbvCfAdj';
}

/**
 * IA field adj VO: { amorChgAdjs, nbvCfAdjs }.
 * Distinguishes from PPE VO which always carries costAdjs/addAdjs/depnChgAdjs keys.
 */
export function isIaFieldAdjustmentsObject(adj: unknown): boolean {
    if (!adj || typeof adj !== 'object' || Array.isArray(adj)) return false;
    const o = adj as Record<string, unknown>;
    if ('amorChgAdjs' in o) return true;
    return 'nbvCfAdjs' in o
        && !('costAdjs' in o)
        && !('addAdjs' in o)
        && !('depnChgAdjs' in o)
        && !('accDepAdjs' in o);
}

export function isIaFieldWpDetail(detail: unknown): boolean {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    return Array.isArray((detail as Record<string, unknown>)[IA_ROW_ARRAY_KEY]);
}

export function hasIaFieldAdjPayloadKeys(detail: unknown): boolean {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const d = detail as Record<string, unknown>;
    return IA_FIELD_ADJ_KEYS.some((k) => k in d)
        || (d.intangibleRowId != null && Number(d.intangibleRowId) > 0);
}

export function hasNestedIaFieldAdjustments(detail: unknown): boolean {
    if (!isIaFieldWpDetail(detail)) return false;
    const rows = (detail as Record<string, unknown>)[IA_ROW_ARRAY_KEY];
    if (!Array.isArray(rows)) return false;
    for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const adj = (row as any).adjustments;
        if (!isIaFieldAdjustmentsObject(adj)) continue;
        for (const {listKey} of IA_FIELD_LIST_MAP) {
            const list = (adj as any)[listKey];
            if (Array.isArray(list) && list.length > 0) return true;
        }
        for (const k of IA_FIELD_ADJ_KEYS) {
            if (Math.abs(Number((row as any)[k]) || 0) >= 0.001) return true;
        }
    }
    return false;
}

export function isIaFieldAdjActiveDetail(detail: unknown): boolean {
    return hasIaFieldAdjPayloadKeys(detail) || hasNestedIaFieldAdjustments(detail);
}

/** AdjustTabsDialog `_synthetic` edit target for IA field WP→WP. */
export function isIaFieldSyntheticDetail(detail: unknown, code?: string | null): boolean {
    if (!isIaStyleFieldFsCode(code, detail)) return false;
    // Only PL-AE-DEP Taxi nest shell uses full intangibleRows UI (not slim synthetic).
    // IA / AIA / ITL field WP→WP keep original slim-synthetic behavior.
    if (
        String(code || '').trim().toUpperCase() === PL_AE_DEP_CODE
        && isIaNamedNestShellDetail(detail)
    ) {
        return false;
    }
    return isIaFieldAdjActiveDetail(detail);
}

/**
 * PL-AE-DEP Taxi field-adj in AdjustTabs non-DTD path (edit / save).
 * Covers slim iaField markers AND GET nest shells with amorChgAdjs — without
 * forcing nest shells onto the slim-synthetic render/load path used by IA/AIA/ITL.
 */
export function isItlDepIaFieldTabsNonDtdDetail(
    detail: unknown,
    code?: string | null,
): boolean {
    return isItlDepIaFieldCode(code, detail);
}

/** Header contains at least one IA field WP→WP line (unlock dynamic Dr/Cr). */
export function isIaFieldAdjHeader(lines: AuditAdjustLine[] | null | undefined): boolean {
    if (!Array.isArray(lines) || lines.length === 0) return false;
    return lines.some((l) => {
        const code = String((l as any)?.code || (l as any)?.scheduleCode || '').trim();
        const raw = (l as any)?.details;
        const detail = Array.isArray(raw) ? raw[0] : raw;
        // DEP only with Taxi iaField evidence — never treat PPE ppeField DEP as IA
        if (!isIaFieldFsCode(code) && !isItlDepIaFieldCode(code, detail)) return false;
        if (isIaFieldSyntheticDetail(detail, code)) return true;
        // Taxi DEP nest (amorChgAdjs) — unlock non-DTD without converting nest → slim on load
        if (isItlDepIaFieldCode(code, detail)) return true;
        const rc = String((detail as any)?.rowClass || '').toLowerCase();
        return rc.startsWith('iafield:');
    });
}

export function buildIaSlimFieldAdjDetail(opts: {
    intangibleRowId?: number | null;
    fieldKey: (typeof IA_FIELD_ADJ_KEYS)[number];
    amount: number;
}): Record<string, any> {
    const amount = Number(opts.amount) || 0;
    const next: Record<string, any> = {adjustment: amount};
    if (Math.abs(amount) >= 0.001) {
        next[opts.fieldKey] = amount;
    }
    const rid = opts.intangibleRowId != null ? Number(opts.intangibleRowId) : NaN;
    if (Number.isFinite(rid) && rid > 0) {
        next.intangibleRowId = rid;
    }
    return next;
}

export type IaAmorNbvCrossFieldKey = (typeof IA_FIELD_ADJ_KEYS)[number];

/**
 * Amor chg ↔ NBV C/F WP→WP only (row-bound slim field adj).
 * Requires positive intangibleRowId; full intangibleRows shells stay on other→IA nest path.
 */
export function isIaAmorNbvCrossFieldDetail(detail: unknown, code?: string | null): boolean {
    const upper = String(code || '').trim().toUpperCase();
    // DEP only when this detail is Taxi iaField (not PPE ppeField)
    if (!isIaFieldFsCode(code) && upper !== PL_AE_DEP_CODE) return false;
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const d = detail as Record<string, any>;
    // Full WP shell with named rows → not Depn↔NBV-style slim cross
    if (isIaNamedNestShellDetail(detail)) {
        return false;
    }
    const rowId = Number(d.intangibleRowId) > 0 ? Number(d.intangibleRowId) : undefined;
    if (!(rowId != null && rowId > 0)) return false;

    const present = IA_FIELD_ADJ_KEYS.filter((k) => {
        const v = Number(d[k]);
        return Number.isFinite(v) && Math.abs(v) >= 0.001;
    });
    if (present.length !== 1) return false;

    const key = present[0];
    if (upper === 'PL-AE-AIA' || upper === PL_AE_DEP_CODE) return key === 'amorChgAdj';
    if (upper === 'BS-NCA-IA' || upper === 'BS-NCA-ITL') return key === 'nbvCfAdj';
    return false;
}

/**
 * other→IA / other→ITL-DEP nest shell: intangibleRows with named items
 * (same outbound shape as applyIaToPaperRowDetail / IntangibleAssets).
 */
export function isIaNamedNestShellDetail(detail: unknown): boolean {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const rows = (detail as Record<string, unknown>)[IA_ROW_ARRAY_KEY];
    if (!Array.isArray(rows)) return false;
    return rows.some((r: any) => String(r?.item || '').trim().length > 0);
}

/**
 * Opposite FS code + field for Amor↔NBV cross-field WP→WP.
 * from Amor (amorChgAdj) → IA nbvCfAdj; from NBV (nbvCfAdj) → AIA amorChgAdj.
 */
export function resolveIaAmorNbvOpposite(
    fromCode?: string | null,
    fromFieldKey?: string | null,
): {toCode: string; toFieldKey: IaAmorNbvCrossFieldKey} | null {
    const upper = String(fromCode || '').trim().toUpperCase();
    const key = String(fromFieldKey || '').trim();
    if (upper === 'PL-AE-AIA' && key === 'amorChgAdj') {
        return {toCode: 'BS-NCA-IA', toFieldKey: 'nbvCfAdj'};
    }
    if (upper === 'BS-NCA-IA' && key === 'nbvCfAdj') {
        return {toCode: 'PL-AE-AIA', toFieldKey: 'amorChgAdj'};
    }
    if (upper === PL_AE_DEP_CODE && key === 'amorChgAdj') {
        return {toCode: 'BS-NCA-ITL', toFieldKey: 'nbvCfAdj'};
    }
    if (upper === 'BS-NCA-ITL' && key === 'nbvCfAdj') {
        return {toCode: PL_AE_DEP_CODE, toFieldKey: 'amorChgAdj'};
    }
    return null;
}

/** Active single cross-field key on detail, if any. */
export function getIaAmorNbvCrossFieldKey(
    detail: unknown,
    code?: string | null,
): IaAmorNbvCrossFieldKey | null {
    if (!isIaAmorNbvCrossFieldDetail(detail, code)) return null;
    const d = detail && typeof detail === 'object' ? (detail as Record<string, any>) : {};
    const present = IA_FIELD_ADJ_KEYS.filter((k) => {
        const v = Number(d[k]);
        return Number.isFinite(v) && Math.abs(v) >= 0.001;
    });
    const key = present[0];
    return key === 'amorChgAdj' || key === 'nbvCfAdj' ? key : null;
}

/** Header has exactly IA+AIA or ITL+DEP (Amor/Depn↔NBV pair only). */
export function isIaAmorNbvCrossOnlyHeader(
    lines: Array<{code?: string | null; scheduleCode?: string | null} | null | undefined> | null | undefined,
): boolean {
    const codes = new Set<string>();
    for (const line of lines || []) {
        const c = String((line as any)?.code || (line as any)?.scheduleCode || '').trim().toUpperCase();
        if (c) codes.add(c);
    }
    if (codes.size !== 2) return false;
    return (codes.has('BS-NCA-IA') && codes.has('PL-AE-AIA'))
        || (codes.has('BS-NCA-ITL') && codes.has(PL_AE_DEP_CODE));
}

function lineHasIaAmorNbvFieldEvidence(detail: unknown, code?: string | null): boolean {
    if (!detail || typeof detail !== 'object') return false;
    if (isIaAmorNbvCrossFieldDetail(detail, code)) return true;
    if (hasNestedIaAmorNbvAdjustments(detail)) return true;
    const d = detail as Record<string, any>;
    if (Math.abs(Number(d.nbvCfAdj) || 0) >= 0.001 || Math.abs(Number(d.amorChgAdj) || 0) >= 0.001) {
        return Number(d.intangibleRowId) > 0;
    }
    // Full WP shell: intangibleRows with nested amorChgAdjs / nbvCfAdjs
    if (Array.isArray(d[IA_ROW_ARRAY_KEY])) {
        for (const row of d[IA_ROW_ARRAY_KEY]) {
            if (!row || typeof row !== 'object') continue;
            const adj = (row as any).adjustments;
            if (adj && typeof adj === 'object') {
                if (Array.isArray(adj.amorChgAdjs) && adj.amorChgAdjs.length > 0) return true;
                if (Array.isArray(adj.nbvCfAdjs) && adj.nbvCfAdjs.length > 0) return true;
            }
            if (Math.abs(Number((row as any).amorChgAdj) || 0) >= 0.001) return true;
            if (Math.abs(Number((row as any).nbvCfAdj) || 0) >= 0.001) return true;
        }
    }
    const asList = Array.isArray(detail) ? detail : ('rowClass' in d ? [d] : null);
    if (!asList) return false;
    return asList.some((item) => {
        if (!item || typeof item !== 'object') return false;
        const rc = String((item as any).rowClass || '').toLowerCase();
        return rc.includes('nbvcfadj') || rc.includes('amorchgadj') || rc.startsWith('iafield:');
    });
}

/** Header has exactly BS-NCA-IA + PL-AE-AIA (Amor↔NBV cross pair only). */
export function isIaAiaCrossOnlyHeader(
    lines: Array<{code?: string | null; scheduleCode?: string | null} | null | undefined> | null | undefined,
): boolean {
    const codes = new Set<string>();
    for (const line of lines || []) {
        const c = String((line as any)?.code || (line as any)?.scheduleCode || '').trim().toUpperCase();
        if (c) codes.add(c);
    }
    return codes.size === 2 && codes.has('BS-NCA-IA') && codes.has('PL-AE-AIA');
}

/**
 * Grand Total Amor↔NBV line-only header (exactly IA + AIA, no row field-adj).
 * GET details may still carry schedule `{adjustments.amorChgAdjusts|nbvCfAdjusts}`.
 */
export function isIaAiaScheduleLevelHeader(
    lines: Array<{
        code?: string | null;
        scheduleCode?: string | null;
        details?: unknown;
    } | null | undefined> | null | undefined,
): boolean {
    if (!isIaAiaCrossOnlyHeader(lines)) return false;
    if (isIaAmorNbvCrossOnlyFieldHeader(lines)) return false;
    for (const line of lines || []) {
        if (!line) continue;
        const code = String((line as any)?.code || (line as any)?.scheduleCode || '').trim();
        const raw = (line as any).details;
        const list = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
        for (const detail of list) {
            if (isIaNamedNestShellDetail(detail)) return false;
            if (lineHasIaAmorNbvFieldEvidence(detail, code)) return false;
        }
    }
    return true;
}

/** Header has exactly BS-NCA-ITL + PL-AE-DEP (Taxi Depn↔NBV schedule pair only). */
export function isItlDepCrossOnlyHeader(
    lines: Array<{code?: string | null; scheduleCode?: string | null} | null | undefined> | null | undefined,
): boolean {
    const codes = new Set<string>();
    for (const line of lines || []) {
        const c = String((line as any)?.code || (line as any)?.scheduleCode || '').trim().toUpperCase();
        if (c) codes.add(c);
    }
    return codes.size === 2 && codes.has('BS-NCA-ITL') && codes.has(PL_AE_DEP_CODE);
}

/**
 * Grand Total Taxi Depn↔NBV line-only header (exactly ITL + DEP, no row field-adj).
 * Mirrors {@link isPpeDepScheduleLevelHeader} / {@link isIaAiaScheduleLevelHeader}.
 */
export function isItlDepScheduleLevelHeader(
    lines: Array<{
        code?: string | null;
        scheduleCode?: string | null;
        details?: unknown;
    } | null | undefined> | null | undefined,
): boolean {
    if (!isItlDepCrossOnlyHeader(lines)) return false;
    if (isIaAmorNbvCrossOnlyFieldHeader(lines)) return false;
    for (const line of lines || []) {
        if (!line) continue;
        const code = String((line as any)?.code || (line as any)?.scheduleCode || '').trim();
        const raw = (line as any).details;
        const list = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
        for (const detail of list) {
            if (isIaNamedNestShellDetail(detail)) return false;
            if (lineHasIaAmorNbvFieldEvidence(detail, code)) return false;
        }
    }
    return true;
}

/** Nested amorChgAdjs / nbvCfAdjs on intangibleRows (GET with working papers). */
function hasNestedIaAmorNbvAdjustments(detail: unknown): boolean {
    if (!isIaFieldWpDetail(detail)) return false;
    const rows = (detail as Record<string, unknown>)[IA_ROW_ARRAY_KEY];
    if (!Array.isArray(rows)) return false;
    for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const adj = (row as Record<string, unknown>).adjustments;
        if (!adj || typeof adj !== 'object' || Array.isArray(adj)) continue;
        const amor = (adj as Record<string, unknown>).amorChgAdjs;
        const nbv = (adj as Record<string, unknown>).nbvCfAdjs;
        if (Array.isArray(amor) && amor.length > 0) return true;
        if (Array.isArray(nbv) && nbv.length > 0) return true;
    }
    return false;
}

/** IA+AIA-only header AND at least one line has Amor↔NBV field evidence. */
export function isIaAmorNbvCrossOnlyFieldHeader(
    lines: Array<{
        code?: string | null;
        scheduleCode?: string | null;
        details?: unknown;
    } | null | undefined> | null | undefined,
): boolean {
    if (!isIaAmorNbvCrossOnlyHeader(lines)) return false;
    for (const line of lines || []) {
        if (!line) continue;
        const code = String((line as any)?.code || (line as any)?.scheduleCode || '').trim();
        if (!isIaFieldFsCode(code) && code.toUpperCase() !== PL_AE_DEP_CODE) continue;
        const raw = (line as any).details;
        const list = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
        for (const detail of list) {
            if (lineHasIaAmorNbvFieldEvidence(detail, code)) return true;
            if (Array.isArray(raw) && lineHasIaAmorNbvFieldEvidence(raw, code)) return true;
        }
    }
    return false;
}

function stripIaRenderAdjustmentsInPlace(detail: Record<string, any> | null | undefined): void {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return;
    if ('adjustments' in detail) delete detail.adjustments;
    const rows = detail[IA_ROW_ARRAY_KEY];
    if (!Array.isArray(rows)) return;
    detail[IA_ROW_ARRAY_KEY] = rows.map((row: any) => {
        if (!row || typeof row !== 'object') return row;
        if (!('adjustments' in row)) return row;
        const {adjustments: _adj, ...rest} = row as Record<string, unknown>;
        return rest;
    });
}

function collectIaFieldAdjFromDetail(
    detail: Record<string, any>,
    headerId?: string,
    code?: string | null,
): {
    found: boolean;
    total: number;
    fieldSums: Partial<Record<(typeof IA_FIELD_ADJ_KEYS)[number], number>>;
    intangibleRowId?: number;
} {
    let total = 0;
    let found = false;
    let intangibleRowId: number | undefined;
    const fieldSums: Partial<Record<(typeof IA_FIELD_ADJ_KEYS)[number], number>> = {};
    const ownedKey = iaOwnedFieldKey(code);
    const listMap = IA_FIELD_LIST_MAP.filter((m) => m.fieldKey === ownedKey);

    const rows = detail[IA_ROW_ARRAY_KEY];
    if (Array.isArray(rows)) {
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            const adj = (row as any).adjustments;
            if (isIaFieldAdjustmentsObject(adj)) {
                for (const {listKey, fieldKey} of listMap) {
                    const sum = sumRentalAdjustmentEntriesStrict(
                        (adj as any)[listKey] as AdjustmentEntry[] | null | undefined,
                        headerId,
                    );
                    if (sum === undefined) continue;
                    found = true;
                    total += sum;
                    fieldSums[fieldKey] = (fieldSums[fieldKey] || 0) + sum;
                    const rid = Number((row as any).id);
                    if (intangibleRowId == null && Number.isFinite(rid) && rid > 0) {
                        intangibleRowId = rid;
                    }
                }
            }
            for (const k of IA_FIELD_ADJ_KEYS) {
                if (k !== ownedKey) continue;
                const v = Number((row as any)[k]);
                if (!Number.isFinite(v) || Math.abs(v) < 0.001) continue;
                found = true;
                total += v;
                fieldSums[k] = (fieldSums[k] || 0) + v;
                const rid = Number((row as any).id);
                if (intangibleRowId == null && Number.isFinite(rid) && rid > 0) {
                    intangibleRowId = rid;
                }
            }
        }
    }

    for (const k of IA_FIELD_ADJ_KEYS) {
        if (k !== ownedKey) continue;
        const top = Number(detail[k]);
        if (!Number.isFinite(top) || Math.abs(top) < 0.001) continue;
        if (fieldSums[k] == null) {
            found = true;
            total += top;
            fieldSums[k] = top;
        }
    }
    if (intangibleRowId == null && Number(detail.intangibleRowId) > 0) {
        intangibleRowId = Number(detail.intangibleRowId);
    }

    return {found, total, fieldSums, intangibleRowId};
}

/**
 * IA WP→WP shape for AdjustTabsDialog:
 * Promote owned field marker + signed adjustment; strip render-only nested adjustments.
 * Prefer slim markers (intangibleRowId + xxxAdj) so re-save only updates entry/amount/xxxAdj.
 */
export function consolidateIaWpFieldAdjustments(
    detail: any,
    code?: string,
    headerId?: string,
    lineAdjustmentAmount?: number,
    lineEntry?: string | null,
    originalEntryOverride?: string | null,
): any {
    if (!detail || typeof detail !== 'object') return detail;
    if (!isIaFieldAdjActiveDetail(detail)) return detail;

    // PL-AE-DEP Taxi other→nest only: keep full intangibleRows shell.
    // IA / AIA / ITL field WP→WP still slim (original behavior).
    if (
        String(code || '').trim().toUpperCase() === PL_AE_DEP_CODE
        && isIaNamedNestShellDetail(detail)
    ) {
        return consolidateIaNestShellDetail(
            detail,
            code,
            headerId,
            lineEntry,
            lineAdjustmentAmount,
            originalEntryOverride,
        );
    }

    const collected = collectIaFieldAdjFromDetail(detail, headerId, code);
    const ownedKey = iaOwnedFieldKey(code);
    const renderEntry = lineEntry === 'Dr' || lineEntry === 'Cr' ? lineEntry : null;
    const existingTop = Number(detail.adjustment);
    const lineAbs = Math.abs(Number(lineAdjustmentAmount) || 0);

    let adjustment = 0;
    if (Number.isFinite(existingTop) && Math.abs(existingTop) >= 0.001) {
        if (renderEntry && lineAbs >= 0.001 && Math.abs(Math.abs(existingTop) - lineAbs) < 0.001) {
            adjustment = signLoanAdjustment(renderEntry, lineAbs, code, originalEntryOverride);
        } else if (renderEntry) {
            adjustment = signLoanAdjustment(renderEntry, Math.abs(existingTop), code, originalEntryOverride);
        } else {
            adjustment = existingTop;
        }
    } else if (collected.found && Math.abs(collected.total) >= 0.001) {
        adjustment = renderEntry
            ? signLoanAdjustment(renderEntry, Math.abs(collected.total), code, originalEntryOverride)
            : collected.total;
    } else if (lineAbs >= 0.001) {
        adjustment = signLoanAdjustment(renderEntry ?? 'Dr', lineAbs, code, originalEntryOverride);
    }

    const rowId = collected.intangibleRowId
        ?? (Number(detail.intangibleRowId) > 0 ? Number(detail.intangibleRowId) : undefined);

    return buildIaSlimFieldAdjDetail({
        intangibleRowId: rowId,
        fieldKey: ownedKey,
        amount: adjustment,
    });
}

/**
 * Keep IntangibleAssets-style nest shell for PL-AE-DEP Taxi hydrate.
 * Prefer GET shape {@code adjustments.{amorChgAdjs,nbvCfAdjs}} (same as ITL / IA WP GET);
 * also expose top-level {@code adjustment} from owned amorChgAdjs for AdjustTabs Dr/Cr render
 * (non-DTD edit/save still strip to slim via {@link toIaFieldAdjSubmitDetail}).
 */
export function consolidateIaNestShellDetail(
    detail: any,
    code?: string | null,
    headerId?: string,
    lineEntry?: string | null,
    lineAdjustmentAmount?: number,
    originalEntryOverride?: string | null,
): any {
    if (!detail || typeof detail !== 'object') return detail;
    const ownedKey = iaOwnedFieldKey(code);
    const rows = Array.isArray(detail[IA_ROW_ARRAY_KEY]) ? detail[IA_ROW_ARRAY_KEY] : [];
    const nextRows = rows.map((row: any) => {
        if (!row || typeof row !== 'object') return row;
        const cleaned: Record<string, any> = {...row};
        const adj = cleaned.adjustments;
        // Keep IntangibleAdjustmentVO shape from getAdjustmentWithWorkingPapers (all headers)
        if (isIaFieldAdjustmentsObject(adj)) {
            cleaned.adjustments = {
                amorChgAdjs: Array.isArray((adj as any).amorChgAdjs)
                    ? [...(adj as any).amorChgAdjs]
                    : [],
                nbvCfAdjs: Array.isArray((adj as any).nbvCfAdjs)
                    ? [...(adj as any).nbvCfAdjs]
                    : [],
            };
            delete cleaned.amorChgAdj;
            delete cleaned.nbvCfAdj;
            delete cleaned.adjustment;
            return cleaned;
        }
        // Legacy scalar nest (no VO): keep owned scalar only
        for (const k of IA_FIELD_ADJ_KEYS) {
            if (k !== ownedKey) delete cleaned[k];
        }
        return cleaned;
    });
    const next: Record<string, any> = {
        ...detail,
        [IA_ROW_ARRAY_KEY]: nextRows,
    };
    delete next.adjustments;
    delete next.intangibleRowId;
    for (const k of IA_FIELD_ADJ_KEYS) {
        delete next[k];
    }
    // AdjustTabs Dr/Cr: surface amorChgAdjs[].adjustment as detail.adjustment (keep nest for WP panel)
    const collected = collectIaFieldAdjFromDetail(
        {[IA_ROW_ARRAY_KEY]: nextRows},
        headerId,
        code,
    );
    const renderEntry = lineEntry === 'Dr' || lineEntry === 'Cr' ? lineEntry : null;
    const lineAbs = Math.abs(Number(lineAdjustmentAmount) || 0);
    if (collected.found && Math.abs(collected.total) >= 0.001) {
        next.adjustment = renderEntry
            ? signLoanAdjustment(
                renderEntry,
                lineAbs >= 0.001 ? lineAbs : Math.abs(collected.total),
                code ?? undefined,
                originalEntryOverride,
            )
            : collected.total;
    } else if (lineAbs >= 0.001 && renderEntry) {
        next.adjustment = signLoanAdjustment(renderEntry, lineAbs, code ?? undefined, originalEntryOverride);
    } else {
        delete next.adjustment;
    }
    return next;
}

/** Keep / rescale owned amorChgAdj|nbvCfAdj with signed line adjustment (AdjustTabs edit). */
export function rescaleIaFieldAdjKeysForAmount(
    detail: Record<string, any> | null | undefined,
    targetAmount: number,
    code?: string | null,
): Record<string, any> {
    const ownedKey = iaOwnedFieldKey(code);
    if (!detail || typeof detail !== 'object') {
        return buildIaSlimFieldAdjDetail({fieldKey: ownedKey, amount: targetAmount});
    }
    const collected = collectIaFieldAdjFromDetail(detail, undefined, code);
    const rowId = Number(detail.intangibleRowId) > 0
        ? Number(detail.intangibleRowId)
        : collected.intangibleRowId;
    return buildIaSlimFieldAdjDetail({
        intangibleRowId: rowId,
        fieldKey: ownedKey,
        amount: targetAmount,
    });
}

/**
 * IA field WP→WP submit details — slim only:
 * `{ intangibleRowId?, nbvCfAdj|amorChgAdj, adjustment }` so orchestrator updates
 * line.entry / adjustmentAmount / attr xxxAdj without rewriting WP row columns.
 */
export function toIaFieldAdjSubmitDetail(
    detail: any,
    signedAdjustment: number,
    code?: string | null,
): Record<string, any> {
    const ownedKey = iaOwnedFieldKey(code);
    const source = detail && typeof detail === 'object' ? detail : {};
    const collected = collectIaFieldAdjFromDetail(source, undefined, code);
    const rowId = Number(source.intangibleRowId) > 0
        ? Number(source.intangibleRowId)
        : collected.intangibleRowId;
    return buildIaSlimFieldAdjDetail({
        intangibleRowId: rowId,
        fieldKey: ownedKey,
        amount: signedAdjustment,
    });
}

/** Convert backend IA WP shell / nested adjustments → slim for AdjustTabs load. */
export function toIaFieldSlimDetailFromBackend(
    detailOrAttrs: any,
    code?: string | null,
    opts?: {
        headerId?: string;
        lineEntry?: string | null;
        lineAdjustmentAmount?: number;
    },
): Record<string, any> | null {
    const detail = Array.isArray(detailOrAttrs) ? detailOrAttrs[0] : detailOrAttrs;
    if (!isIaFieldFsCode(code) && !isItlDepIaFieldCode(code, detail)) {
        // Flat attr array: allow DEP when any attr is iaField:*
        if (!(Array.isArray(detailOrAttrs) && detailOrAttrs.some((a: any) =>
            String(a?.rowClass || '').toLowerCase().startsWith('iafield:')
        ) && String(code || '').trim().toUpperCase() === PL_AE_DEP_CODE)) {
            return null;
        }
    }
    if (!detail || typeof detail !== 'object') return null;
    if (!isIaFieldAdjActiveDetail(detail) && !String((detail as any).rowClass || '').toLowerCase().startsWith('iafield:')) {
        return null;
    }
    // PL-AE-DEP Taxi nest shell only — keep IntangibleAssets shape (not slim).
    // BS-NCA-IA / PL-AE-AIA / BS-NCA-ITL keep original slim hydrate.
    if (
        String(code || '').trim().toUpperCase() === PL_AE_DEP_CODE
        && isIaNamedNestShellDetail(detail)
    ) {
        return consolidateIaNestShellDetail(detail, code, opts?.headerId, opts?.lineEntry, opts?.lineAdjustmentAmount);
    }
    // Flat attr array shape
    if (Array.isArray(detailOrAttrs) && detailOrAttrs[0]?.rowClass) {
        const ownedKey = iaOwnedFieldKey(code);
        const marker = `iafield:${ownedKey}`.toLowerCase();
        let amount = 0;
        let rowId: number | undefined;
        for (const attr of detailOrAttrs) {
            const rc = String(attr?.rowClass || '').toLowerCase();
            if (!rc.startsWith('iafield:')) continue;
            if (rc !== marker && !rc.endsWith(`:${ownedKey.toLowerCase()}`)) continue;
            if (opts?.headerId != null) {
                const hid = attr.adjustHeaderId ?? attr.headerId;
                if (hid != null && String(hid) !== String(opts.headerId)) continue;
            }
            amount += Number(attr.adjustment) || 0;
            const rid = Number(attr.rowId);
            if (rowId == null && Number.isFinite(rid) && rid > 0) rowId = rid;
        }
        const lineAbs = Math.abs(Number(opts?.lineAdjustmentAmount) || 0);
        const renderEntry = opts?.lineEntry === 'Dr' || opts?.lineEntry === 'Cr' ? opts.lineEntry : null;
        const signed = renderEntry
            ? signLoanAdjustment(renderEntry, lineAbs >= 0.001 ? lineAbs : Math.abs(amount), code ?? undefined)
            : (lineAbs >= 0.001 ? (opts!.lineEntry === 'Cr' ? -lineAbs : lineAbs) : amount);
        return buildIaSlimFieldAdjDetail({intangibleRowId: rowId, fieldKey: ownedKey, amount: signed});
    }
    return consolidateIaWpFieldAdjustments(
        detail,
        code ?? undefined,
        opts?.headerId,
        opts?.lineAdjustmentAmount,
        opts?.lineEntry,
    );
}

/** PPE WP shape: any xxxRow array (structural only — not enough to enable field WP→WP paths). */
export function isPpeFieldWpDetail(detail: unknown): boolean {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const d = detail as Record<string, unknown>;
    return PPE_ROW_ARRAY_KEYS.some((k) => Array.isArray(d[k]));
}

/**
 * Slim WP→WP / AdjustTabs markers: top-level field keys only.
 * Nested-on-row scalars are PPE fromPaper/submit outbound shape — detected separately
 * via {@link hasNestedPpeFieldAdjScalarsPresent} so shared classify/rescale stay unchanged.
 */
export function hasPpeFieldAdjPayloadKeys(detail: unknown): boolean {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;
    const d = detail as Record<string, unknown>;
    return PPE_FIELD_ADJ_KEYS.some((k) => k in d);
}

/** True when any xxxRow carries scalar costAdj/addAdj/nbvCfAdj/… (PPE outbound shape). */
export function hasNestedPpeFieldAdjScalarsPresent(detail: unknown): boolean {
    const nested = collectNestedPpeFieldAdjScalars(
        detail && typeof detail === 'object' ? (detail as Record<string, any>) : null,
    );
    return PPE_FIELD_ADJ_KEYS.some((k) => Math.abs(Number(nested.fieldSums[k]) || 0) >= 0.001);
}

/**
 * Collect scalar field-adj keys nested on xxxRow entities (PPE fromPaper / submit shape).
 * Does not include render-only `row.adjustments.*` lists.
 */
export function collectNestedPpeFieldAdjScalars(detail: Record<string, any> | null | undefined): {
    fieldSums: Partial<Record<(typeof PPE_FIELD_ADJ_KEYS)[number], number>>;
    ppeRowId?: number;
} {
    const fieldSums: Partial<Record<(typeof PPE_FIELD_ADJ_KEYS)[number], number>> = {};
    let ppeRowId: number | undefined;
    if (!detail || typeof detail !== 'object') return {fieldSums};

    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = detail[rowKey];
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            let rowHasField = false;
            for (const k of PPE_FIELD_ADJ_KEYS) {
                const v = Number((row as any)[k]);
                if (!Number.isFinite(v) || Math.abs(v) < 0.001) continue;
                fieldSums[k] = (fieldSums[k] || 0) + v;
                rowHasField = true;
            }
            if (rowHasField && ppeRowId == null) {
                const rid = Number((row as any).id);
                if (Number.isFinite(rid) && rid > 0) ppeRowId = rid;
            }
        }
    }
    return {fieldSums, ppeRowId};
}

/**
 * Bridge for shared AdjustTabs / rescale / consolidate: promote nested-on-row field
 * scalars to top-level markers so existing shared logic stays unchanged.
 * Scoped — does not nest; outbound nesting is only {@link nestPpeFieldAdjOntoTargetRow}.
 */
function hoistNestedPpeFieldAdjScalarsForDraft(
    detail: Record<string, any>,
): Record<string, any> {
    const nested = collectNestedPpeFieldAdjScalars(detail);
    const hasNested = PPE_FIELD_ADJ_KEYS.some(
        (k) => Math.abs(Number(nested.fieldSums[k]) || 0) >= 0.001,
    );
    if (!hasNested) return detail;

    const next: Record<string, any> = {...detail};
    for (const k of PPE_FIELD_ADJ_KEYS) {
        const v = Number(nested.fieldSums[k]) || 0;
        if (Math.abs(v) < 0.001) continue;
        if (!(Math.abs(Number(next[k]) || 0) >= 0.001)) {
            next[k] = v;
        }
    }
    if (!(Number(next.ppeRowId) > 0) && nested.ppeRowId != null) {
        next.ppeRowId = nested.ppeRowId;
    }
    // Strip scalars from rows so shared paths only see top-level markers
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = next[rowKey];
        if (!Array.isArray(rows)) continue;
        next[rowKey] = rows.map((row) => {
            if (!row || typeof row !== 'object') return row;
            const cleaned = {...row};
            for (const k of PPE_FIELD_ADJ_KEYS) delete cleaned[k];
            return cleaned;
        });
    }
    return next;
}

/**
 * Nest field-adj scalars onto the xxxRow with matching id; drop empty xxxRow keys and
 * top-level `adjustment` / `ppeRowId` / field markers.
 * **Outbound only** — PropertyPlantEquipment fromPaper assembly + {@link toPpeFieldAdjSubmitDetail}.
 * Do not call from shared AdjustTabs / classify / rescale paths.
 *
 * Empty-category toPaper: after draft hoist, field keys sit top-level while the target
 * xxxRow keeps a no-id shell — re-nest onto that shell (not top-level).
 */
export function nestPpeFieldAdjOntoTargetRow(
    detail: Record<string, any> | null | undefined,
    opts?: {
        ppeRowId?: number | null;
        fieldAmounts?: Partial<Record<(typeof PPE_FIELD_ADJ_KEYS)[number], number>>;
        /** Draft/UI only — keep signed line amount at top-level. Submit / fromPaper omit it. */
        keepTopLevelAdjustment?: boolean;
        topLevelAdjustment?: number;
    },
): Record<string, any> {
    const source = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : {};
    const next: Record<string, any> = {...source};

    // Remember rows that already carry field scalars (incl. empty shells without id)
    // so we can re-nest when ppeRowId is absent.
    const sourceSlots: Array<{rowKey: string; index: number}> = [];
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = next[rowKey];
        if (!Array.isArray(rows)) continue;
        rows.forEach((row, index) => {
            if (!row || typeof row !== 'object') return;
            if (PPE_FIELD_ADJ_KEYS.some((k) => Math.abs(Number((row as any)[k]) || 0) >= 0.001)) {
                sourceSlots.push({rowKey, index});
            }
        });
    }

    const nested = collectNestedPpeFieldAdjScalars(next);
    const fieldAmounts: Partial<Record<(typeof PPE_FIELD_ADJ_KEYS)[number], number>> = {
        ...nested.fieldSums,
        ...(opts?.fieldAmounts || {}),
    };
    for (const k of PPE_FIELD_ADJ_KEYS) {
        const top = Number(next[k]);
        if (Number.isFinite(top) && Math.abs(top) >= 0.001 && fieldAmounts[k] == null) {
            fieldAmounts[k] = top;
        }
    }

    let ppeRowId = opts?.ppeRowId != null && Number(opts.ppeRowId) > 0
        ? Number(opts.ppeRowId)
        : undefined;
    if (ppeRowId == null && next.ppeRowId != null && Number(next.ppeRowId) > 0) {
        ppeRowId = Number(next.ppeRowId);
    }
    if (ppeRowId == null) ppeRowId = nested.ppeRowId;

    // Clear prior nested scalars so a retargeted rowId does not leave stale markers
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = next[rowKey];
        if (!Array.isArray(rows)) continue;
        next[rowKey] = rows.map((row) => {
            if (!row || typeof row !== 'object') return row;
            const cleaned = {...row};
            for (const k of PPE_FIELD_ADJ_KEYS) delete cleaned[k];
            return cleaned;
        });
    }

    const activeKeys = PPE_FIELD_ADJ_KEYS.filter(
        (k) => Math.abs(Number(fieldAmounts[k]) || 0) >= 0.001,
    );

    const nestOntoSlot = (slot: {rowKey: string; index: number}): boolean => {
        const rows = Array.isArray(next[slot.rowKey]) ? [...next[slot.rowKey]] : [];
        const target = rows[slot.index];
        if (!target || typeof target !== 'object') return false;
        const updated = {...target};
        for (const k of activeKeys) {
            updated[k] = Number(fieldAmounts[k]) || 0;
        }
        rows[slot.index] = updated;
        next[slot.rowKey] = rows;
        for (const k of PPE_FIELD_ADJ_KEYS) delete next[k];
        delete next.ppeRowId;
        return true;
    };

    if (ppeRowId != null && activeKeys.length > 0) {
        let nestedOk = false;
        for (const rowKey of PPE_ROW_ARRAY_KEYS) {
            const rows = next[rowKey];
            if (!Array.isArray(rows)) continue;
            next[rowKey] = rows.map((row) => {
                if (!row || typeof row !== 'object') return row;
                if (Number((row as any).id) !== ppeRowId) return row;
                nestedOk = true;
                const updated = {...row};
                for (const k of activeKeys) {
                    updated[k] = Number(fieldAmounts[k]) || 0;
                }
                return updated;
            });
        }
        if (!nestedOk) {
            // Target row missing from shell — keep top-level markers (legacy fallback)
            for (const k of activeKeys) next[k] = Number(fieldAmounts[k]) || 0;
            next.ppeRowId = ppeRowId;
        }
    } else if (activeKeys.length > 0 && sourceSlots.length > 0) {
        // Empty-category shell that still carried field scalars before clear
        const slot = sourceSlots[0];
        const rows = Array.isArray(next[slot.rowKey]) ? next[slot.rowKey] : [];
        const target = rows[slot.index];
        const targetId = Number((target as any)?.id);
        if (target && typeof target === 'object' && !(Number.isFinite(targetId) && targetId > 0)) {
            nestOntoSlot(slot);
        } else {
            for (const k of activeKeys) next[k] = Number(fieldAmounts[k]) || 0;
        }
    } else if (activeKeys.length > 0) {
        // Draft hoist left field keys top-level; empty shell remains on target xxxRow without id
        const shellSlot = findEmptyPpeCategoryShellSlot(next);
        if (shellSlot && nestOntoSlot(shellSlot)) {
            // nested onto empty shell
        } else {
            for (const k of activeKeys) next[k] = Number(fieldAmounts[k]) || 0;
        }
    }

    // Preferred persist shape: no top-level field markers / ppeRowId when nested succeeded
    if (ppeRowId != null && activeKeys.length > 0) {
        let hasNested = false;
        for (const rowKey of PPE_ROW_ARRAY_KEYS) {
            const rows = next[rowKey];
            if (!Array.isArray(rows)) continue;
            for (const row of rows) {
                if (!row || typeof row !== 'object') continue;
                if (Number((row as any).id) !== ppeRowId) continue;
                if (activeKeys.some((k) => Math.abs(Number((row as any)[k]) || 0) >= 0.001)) {
                    hasNested = true;
                }
            }
        }
        if (hasNested) {
            for (const k of PPE_FIELD_ADJ_KEYS) delete next[k];
            delete next.ppeRowId;
        }
    }

    delete next['ppeNbvSplit'];
    delete next.adjustments;
    stripPpeRenderAdjustmentsInPlace(next);

    if (opts?.keepTopLevelAdjustment) {
        const adj = opts.topLevelAdjustment;
        if (adj != null && Number.isFinite(adj)) next.adjustment = adj;
    } else {
        delete next.adjustment;
    }

    // Never leave orphan ppeRowId without a nested/top-level field marker
    if (!PPE_FIELD_ADJ_KEYS.some((k) => Math.abs(Number(next[k]) || 0) >= 0.001)) {
        let hasNestedField = false;
        for (const rowKey of PPE_ROW_ARRAY_KEYS) {
            const rows = next[rowKey];
            if (!Array.isArray(rows)) continue;
            for (const row of rows) {
                if (!row || typeof row !== 'object') continue;
                if (PPE_FIELD_ADJ_KEYS.some((k) => Math.abs(Number((row as any)[k]) || 0) >= 0.001)) {
                    hasNestedField = true;
                    break;
                }
            }
            if (hasNestedField) break;
        }
        if (!hasNestedField) delete next.ppeRowId;
    }

    return omitEmptyPpeRowKeys(next);
}

/**
 * Empty-category toPaper shell: no positive id and no meaningful cost/item data.
 * Returns a slot only when exactly one such shell exists (avoids mis-nesting onto
 * unrelated blank rows). Outbound nest only — not shared AdjustTabs paths.
 */
function findEmptyPpeCategoryShellSlot(
    detail: Record<string, any>,
): {rowKey: string; index: number} | null {
    let found: {rowKey: string; index: number} | null = null;
    let count = 0;
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = detail[rowKey];
        if (!Array.isArray(rows)) continue;
        for (let index = 0; index < rows.length; index++) {
            const row = rows[index];
            if (!row || typeof row !== 'object') continue;
            const id = Number((row as any).id);
            if (Number.isFinite(id) && id > 0) continue;
            const hasItem = String((row as any).item || '').trim().length > 0;
            const hasCost = (row as any).costBf != null && Number((row as any).costBf) !== 0;
            const hasAdd = (row as any).additions != null && Number((row as any).additions) !== 0;
            if (!hasItem && !hasCost && !hasAdd) {
                count++;
                if (count === 1) found = {rowKey, index};
            }
        }
    }
    return count === 1 ? found : null;
}

/** GET shape: nested row.adjustments.{costAdjs|addAdjs|…} with at least one non-empty list. */
export function hasNestedPpeFieldAdjustments(detail: unknown): boolean {
    if (!isPpeFieldWpDetail(detail)) return false;
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = (detail as Record<string, unknown>)[rowKey];
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            const adj = (row as Record<string, unknown>).adjustments;
            if (!isPpeFieldAdjustmentsObject(adj)) continue;
            for (const {listKey} of PPE_FIELD_LIST_MAP) {
                const list = (adj as Record<string, unknown>)[listKey];
                if (Array.isArray(list) && list.length > 0) return true;
            }
        }
    }
    return false;
}

/** Nested nbvCfAdjs / depnChgAdjs only (Depn↔NBV); ignores cost/add/accDep lists. */
function hasNestedPpeDepnNbvAdjustments(detail: unknown): boolean {
    if (!isPpeFieldWpDetail(detail)) return false;
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = (detail as Record<string, unknown>)[rowKey];
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            const adj = (row as Record<string, unknown>).adjustments;
            if (!adj || typeof adj !== 'object' || Array.isArray(adj)) continue;
            const nbv = (adj as Record<string, unknown>).nbvCfAdjs;
            const depn = (adj as Record<string, unknown>).depnChgAdjs;
            if (Array.isArray(nbv) && nbv.length > 0) return true;
            if (Array.isArray(depn) && depn.length > 0) return true;
        }
    }
    return false;
}

/**
 * True only for PPE field WP→WP payloads (panel keys, nested field-adj lists, or
 * outbound nested-on-row scalars). Plain BS-NCA-PPE / PL-AE-DEP WP with xxxRow alone
 * must stay on existing paths.
 */
export function isPpeFieldAdjActiveDetail(detail: unknown): boolean {
    return hasPpeFieldAdjPayloadKeys(detail)
        || hasNestedPpeFieldAdjustments(detail)
        || hasNestedPpeFieldAdjScalarsPresent(detail);
}

/**
 * FsSelectionDrawer PPE 分类 toPaper：xxxRow 上写了 row.adjustment，且非 slim field keys。
 * 与 field WP→WP（costAdj/…）及空分类 nbvCfAdj/depnChgAdj 区分。
 */
export function isPpeCategoryRowBoundDetail(detail: unknown): boolean {
    if (!isPpeFieldWpDetail(detail)) return false;
    if (hasPpeFieldAdjPayloadKeys(detail) || hasNestedPpeFieldAdjScalarsPresent(detail)) return false;
    const d = detail as Record<string, unknown>;
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = d[rowKey];
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            if (Math.abs(Number((row as any).adjustment) || 0) >= 0.001) return true;
        }
    }
    return false;
}

/**
 * AdjustTabsDialog `_synthetic` edit target for PPE field WP→WP.
 * Requires both FS code and field-adj evidence — not structural xxxRow alone.
 * Category toPaper（row.adjustment）不走 field 精简路径。
 */
export function isPpeFieldSyntheticDetail(detail: unknown, code?: string | null): boolean {
    if (!isPpeFieldFsCode(code)) return false;
    if (isPpeCategoryRowBoundDetail(detail)) return false;
    return isPpeFieldAdjActiveDetail(detail);
}

const PPE_NBV_CF_FIELD_KEY = 'nbvCfAdj' as const;
const PPE_DEPN_CHG_FIELD_KEY = 'depnChgAdj' as const;

export type PpeDepnNbvCrossFieldKey = typeof PPE_NBV_CF_FIELD_KEY | typeof PPE_DEPN_CHG_FIELD_KEY;

/**
 * Line-level NBV / opposite path: `details: []` (or blank object with at most adjustment scalars).
 * Used by Grand Total / Total NBV c/f — no ppeRowId / field attr shell.
 */
export function isBlankWpDetail(detail: unknown): boolean {
    if (detail == null) return true;
    if (Array.isArray(detail)) return detail.length === 0;
    if (typeof detail !== 'object') return true;
    const d = detail as Record<string, any>;
    const keys = Object.keys(d).filter((k) => {
        const v = d[k];
        if (v == null || v === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
    });
    if (keys.length === 0) return true;
    const onlyAdjScalars = keys.every((k) => k === 'adjustment' || k === 'adjustmentAmount');
    return onlyAdjScalars;
}

/** Signed amount for Grand Total / Total NBV adj context: Dr → +, Cr → −. */
export function signedAdjFromLineEntry(
    amount: number | null | undefined,
    entry?: string | null,
): number {
    const abs = Math.abs(Number(amount) || 0);
    if (abs < 0.001) return 0;
    const e = String(entry || '').trim().toLowerCase();
    return e === 'cr' || e === 'cr.' || e === 'credit' ? -abs : abs;
}

/**
 * BS-NCA-PPE / BS-NCA-IA / BS-NCA-ITL / CommonInvestment with blank details only.
 * Used for Grand Total / Total NBV (no attr) → submit {@code adjustmentAmount} + {@code details: []}.
 * Does NOT treat a full WP shell as line-only (avoids hijacking normal PPE/IA/ITL WP paths).
 */
export function isAssetLineOnlyAmountSubmit(
    code?: string | null,
    detail?: unknown,
): boolean {
    const upper = String(code || '').trim().toUpperCase();
    if (
        upper !== 'BS-NCA-PPE'
        && upper !== 'BS-NCA-IA'
        && upper !== 'BS-NCA-ITL'
        && upper !== 'BS-NCA-ISUB'
        && upper !== 'BS-NCA-IASS'
        && upper !== 'BS-CA-IOT'
    ) {
        return false;
    }
    return isBlankWpDetail(detail);
}

/**
 * Schedule-level Depn↔NBV from Grand Total NBV (empty details on BS-NCA-PPE).
 * DEP reverse is handled in tryBuild by toCode (blank DEP → PPE).
 */
export function isPpeScheduleLevelNbvOrDepnContext(
    fromCode?: string | null,
    fromDetail?: unknown,
): boolean {
    const upper = String(fromCode || '').trim().toUpperCase();
    if (upper !== 'BS-NCA-PPE') return false;
    if (isPpeDepnNbvCrossFieldDetail(fromDetail, fromCode)) return false;
    if (isPpeCategoryRowBoundDetail(fromDetail)) return false;
    return isBlankWpDetail(fromDetail);
}

/**
 * Schedule-level Amor/Depn↔NBV for IA / ITL / CommonInvestment Total NBV (empty details).
 * DEP→ITL reverse is handled in tryBuild by toCode.
 */
export function isIaScheduleLevelNbvOrAmorContext(
    fromCode?: string | null,
    fromDetail?: unknown,
): boolean {
    // IA_FIELD_FS_CODES includes IA/AIA/ITL + ISUB/IASS/IOT
    if (!isIaFieldFsCode(fromCode)) return false;
    if (isIaAmorNbvCrossFieldDetail(fromDetail, fromCode)) return false;
    if (isIaNamedNestShellDetail(fromDetail)) return false;
    return isBlankWpDetail(fromDetail);
}

/**
 * Depn chg Final ↔ NBV c/f WP→WP only (row-bound slim field adj).
 * Requires positive ppeRowId so empty-category nbvCfAdj/depnChgAdj shells stay on
 * existing category / full-shell paths.
 * Schedule-level (Grand Total) NBV uses {@link isPpeScheduleLevelNbvOrDepnContext} instead.
 */
export function isPpeDepnNbvCrossFieldDetail(detail: unknown, code?: string | null): boolean {
    if (!isPpeFieldFsCode(code)) return false;
    if (isPpeCategoryRowBoundDetail(detail)) return false;
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return false;

    const hoisted = hoistNestedPpeFieldAdjScalarsForDraft(detail as Record<string, any>);
    const ppeRowId = Number(hoisted.ppeRowId) > 0
        ? Number(hoisted.ppeRowId)
        : collectNestedPpeFieldAdjScalars(hoisted).ppeRowId;
    if (!(ppeRowId != null && ppeRowId > 0)) return false;

    const present = PPE_FIELD_ADJ_KEYS.filter((k) => {
        const v = Number(hoisted[k]);
        return Number.isFinite(v) && Math.abs(v) >= 0.001;
    });
    if (present.length !== 1) return false;

    const upper = String(code || '').trim().toUpperCase();
    const key = present[0];
    if (upper === 'PL-AE-DEP') return key === PPE_DEPN_CHG_FIELD_KEY;
    if (upper === 'BS-NCA-PPE') return key === PPE_NBV_CF_FIELD_KEY;
    return false;
}

/** Slim draft/toPaper detail: adjustment + optional ppeRowId + single field key (no xxxRow). */
export function buildPpeSlimFieldAdjDetail(opts: {
    ppeRowId?: number | null;
    fieldKey: (typeof PPE_FIELD_ADJ_KEYS)[number];
    amount: number;
}): Record<string, any> {
    const amount = Number(opts.amount) || 0;
    const next: Record<string, any> = {adjustment: amount};
    if (Math.abs(amount) >= 0.001) {
        next[opts.fieldKey] = amount;
    }
    const rid = opts.ppeRowId != null ? Number(opts.ppeRowId) : NaN;
    if (Number.isFinite(rid) && rid > 0) {
        next.ppeRowId = rid;
    }
    return next;
}

/**
 * Opposite FS code + field for Depn↔NBV cross-field WP→WP.
 * from Depn (depnChgAdj) → PPE nbvCfAdj; from NBV (nbvCfAdj) → DEP depnChgAdj.
 */
export function resolvePpeDepnNbvOpposite(
    fromCode?: string | null,
    fromFieldKey?: string | null,
): {toCode: string; toFieldKey: PpeDepnNbvCrossFieldKey} | null {
    const upper = String(fromCode || '').trim().toUpperCase();
    const key = String(fromFieldKey || '').trim();
    // Require explicit field key — do not infer from code alone (avoids broad exclusivity bypass)
    if (upper === 'PL-AE-DEP' && key === PPE_DEPN_CHG_FIELD_KEY) {
        return {toCode: 'BS-NCA-PPE', toFieldKey: PPE_NBV_CF_FIELD_KEY};
    }
    if (upper === 'BS-NCA-PPE' && key === PPE_NBV_CF_FIELD_KEY) {
        return {toCode: 'PL-AE-DEP', toFieldKey: PPE_DEPN_CHG_FIELD_KEY};
    }
    return null;
}

/** Active single cross-field key on detail, if any. */
export function getPpeDepnNbvCrossFieldKey(
    detail: unknown,
    code?: string | null,
): PpeDepnNbvCrossFieldKey | null {
    if (!isPpeDepnNbvCrossFieldDetail(detail, code)) return null;
    const hoisted = hoistNestedPpeFieldAdjScalarsForDraft(
        detail && typeof detail === 'object' ? (detail as Record<string, any>) : {},
    );
    const present = PPE_FIELD_ADJ_KEYS.filter((k) => {
        const v = Number(hoisted[k]);
        return Number.isFinite(v) && Math.abs(v) >= 0.001;
    });
    const key = present[0];
    if (key === PPE_NBV_CF_FIELD_KEY || key === PPE_DEPN_CHG_FIELD_KEY) return key;
    return null;
}

/**
 * Header has exactly BS-NCA-PPE + PL-AE-DEP (Depn↔NBV cross-field pair only).
 * Codes-only check — prefer {@link isPpeDepnNbvCrossOnlyFieldHeader} for behavior gates.
 */
export function isPpeDepnNbvCrossOnlyHeader(
    lines: Array<{code?: string | null; scheduleCode?: string | null} | null | undefined> | null | undefined,
): boolean {
    const codes = new Set<string>();
    for (const line of lines || []) {
        const c = String((line as any)?.code || (line as any)?.scheduleCode || '').trim().toUpperCase();
        if (c) codes.add(c);
    }
    return codes.size === 2 && codes.has('BS-NCA-PPE') && codes.has('PL-AE-DEP');
}

/**
 * Grand Total Depn↔NBV line-only header (exactly PPE + DEP, no row field-adj / category / Taxi nest).
 * GET details may still carry schedule `{adjustments.depnChgAdjusts|nbvCfAdjusts}`.
 */
export function isPpeDepScheduleLevelHeader(
    lines: Array<{
        code?: string | null;
        scheduleCode?: string | null;
        details?: unknown;
    } | null | undefined> | null | undefined,
): boolean {
    if (!isPpeDepnNbvCrossOnlyHeader(lines)) return false;
    if (isPpeDepnNbvCrossOnlyFieldHeader(lines)) return false;
    for (const line of lines || []) {
        if (!line) continue;
        const code = String((line as any)?.code || (line as any)?.scheduleCode || '').trim();
        const raw = (line as any).details;
        const list = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
        for (const detail of list) {
            if (isPpeCategoryRowBoundDetail(detail)) return false;
            if (isIaNamedNestShellDetail(detail)) return false;
            if (isPpeFieldSyntheticDetail(detail, code)) return false;
        }
    }
    return true;
}

/** Dr keeps signed `adjustment`; Cr stores the absolute value. */
export function resolvePpeDepScheduleSubmitAmount(
    entry: string | null | undefined,
    adjustment: number,
): number {
    const value = Number(adjustment) || 0;
    const upper = String(entry || '').trim().toUpperCase();
    return upper.startsWith('C') ? Math.abs(value) : value;
}

/** True when a line detail carries Depn↔NBV field evidence (flat / nested / slim). */
function lineHasPpeDepnNbvFieldEvidence(
    detail: unknown,
    code?: string | null,
): boolean {
    if (!detail || typeof detail !== 'object') return false;
    if (isPpeDepnNbvCrossFieldDetail(detail, code)) return true;
    if (hasNestedPpeDepnNbvAdjustments(detail)) return true;
    const d = detail as Record<string, any>;
    if (Math.abs(Number(d.nbvCfAdj) || 0) >= 0.001 || Math.abs(Number(d.depnChgAdj) || 0) >= 0.001) {
        return Number(d.ppeRowId) > 0;
    }
    const asList = Array.isArray(detail) ? detail : ('rowClass' in d ? [d] : null);
    if (!asList) return false;
    return asList.some((item) => {
        if (!item || typeof item !== 'object') return false;
        const rc = String((item as any).rowClass || '').toLowerCase();
        return rc.includes('nbvcfadj') || rc.includes('depnchgadj');
    });
}

/**
 * PPE+DEP-only header AND at least one line has Depn↔NBV field evidence.
 * Use this for hydrate / edit / GET consolidate gates — not mere code coexistence.
 */
export function isPpeDepnNbvCrossOnlyFieldHeader(
    lines: Array<{
        code?: string | null;
        scheduleCode?: string | null;
        details?: unknown;
    } | null | undefined> | null | undefined,
): boolean {
    if (!isPpeDepnNbvCrossOnlyHeader(lines)) return false;
    for (const line of lines || []) {
        if (!line) continue;
        const code = String((line as any)?.code || (line as any)?.scheduleCode || '').trim();
        if (!isPpeFieldFsCode(code)) continue;
        const raw = (line as any).details;
        const list = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
        for (const detail of list) {
            if (lineHasPpeDepnNbvFieldEvidence(detail, code)) return true;
            // Flat attr list passed as line.details itself
            if (Array.isArray(raw) && lineHasPpeDepnNbvFieldEvidence(raw, code)) return true;
        }
    }
    return false;
}

/**
 * Hydrate GET detail → AdjustTabs slim shape for Depn↔NBV-only headers:
 * `{ [nbvCfAdj|depnChgAdj]: signed, ppeRowId }` (+ draft `adjustment`).
 * Ignores cross-owned nested lists on the WP shell (e.g. depnChgAdjs under BS-NCA-PPE).
 */
export function toPpeDepnNbvCrossSlimDetailFromBackend(
    detailOrAttrs: any,
    code?: string | null,
    opts?: {
        headerId?: string;
        lineEntry?: string | null;
        lineAdjustmentAmount?: number;
    },
): Record<string, any> | null {
    const upper = String(code || '').trim().toUpperCase();
    if (upper !== 'BS-NCA-PPE' && upper !== 'PL-AE-DEP') return null;
    const ownedKey: PpeDepnNbvCrossFieldKey = upper === 'PL-AE-DEP'
        ? PPE_DEPN_CHG_FIELD_KEY
        : PPE_NBV_CF_FIELD_KEY;

    // Flat attr list or single attr object
    const asAttrList = Array.isArray(detailOrAttrs)
        ? detailOrAttrs
        : (detailOrAttrs && typeof detailOrAttrs === 'object' && 'rowClass' in detailOrAttrs
            ? [detailOrAttrs]
            : null);
    if (asAttrList) {
        const first = asAttrList[0];
        const rc = String((first as any)?.rowClass || '').toLowerCase();
        if (!rc.startsWith('ppefield:') && !PPE_FIELD_ADJ_KEYS.some((k) => k.toLowerCase() === rc)) {
            return null;
        }
        const transformed = transformFlatPpeFieldAttrDetails(
            asAttrList,
            opts?.lineEntry,
            code,
        );
        if (!transformed) return null;
        const amount = Number(transformed[ownedKey]) || Number(transformed.adjustment) || 0;
        const ppeRowId = Number(transformed.ppeRowId) > 0 ? Number(transformed.ppeRowId) : undefined;
        if (!(ppeRowId != null && ppeRowId > 0) || Math.abs(amount) < 0.001) return null;
        // Drop shell if transformFlat wrapped BS — keep only owned slim fields
        return buildPpeSlimFieldAdjDetail({ppeRowId, fieldKey: ownedKey, amount});
    }

    if (!detailOrAttrs || typeof detailOrAttrs !== 'object') return null;

    // Already slim
    if (isPpeDepnNbvCrossFieldDetail(detailOrAttrs, code)) {
        const amount = Number((detailOrAttrs as any)[ownedKey])
            || Number((detailOrAttrs as any).adjustment)
            || 0;
        const ppeRowId = Number((detailOrAttrs as any).ppeRowId);
        return buildPpeSlimFieldAdjDetail({ppeRowId, fieldKey: ownedKey, amount});
    }

    // Full WP shell with nested adjustments.* lists (GET with working papers)
    if (!isPpeFieldWpDetail(detailOrAttrs) && !hasNestedPpeFieldAdjustments(detailOrAttrs)) {
        return null;
    }
    const consolidated = consolidatePpeWpFieldAdjustments(
        detailOrAttrs,
        code ?? undefined,
        opts?.headerId,
        opts?.lineAdjustmentAmount,
        opts?.lineEntry,
        null,
    );
    const amount = Number(consolidated?.[ownedKey]) || Number(consolidated?.adjustment) || 0;
    const ppeRowId = Number(consolidated?.ppeRowId) > 0
        ? Number(consolidated.ppeRowId)
        : collectPpeFieldAdjFromDetail(detailOrAttrs, opts?.headerId, code ?? undefined).ppeRowId;
    if (!(ppeRowId != null && ppeRowId > 0) || Math.abs(amount) < 0.001) return null;
    return buildPpeSlimFieldAdjDetail({ppeRowId, fieldKey: ownedKey, amount});
}

/** Client-only marker: BS-NCA-PPE line = ΔNBV while costAdj/addAdj stays ΔCost. Never sent to backend. */
export const PPE_NBV_SPLIT_FLAG = 'ppeNbvSplit';

/**
 * Strip render-only `adjustments` (top-level + nested on xxxRow / testRow).
 * Used on assemble / submit payloads — PropertyPlantEquipment UI still reads GET adjustments for badges.
 */
export function stripPpeRenderAdjustmentsInPlace(detail: Record<string, any> | null | undefined): void {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return;
    if ('adjustments' in detail) {
        delete detail.adjustments;
    }
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = detail[rowKey];
        if (!Array.isArray(rows)) continue;
        detail[rowKey] = rows.map((row) => {
            if (!row || typeof row !== 'object') return row;
            if (!('adjustments' in row)) return row;
            const {adjustments: _adj, ...rest} = row as Record<string, unknown>;
            return rest;
        });
    }
    if (Array.isArray(detail.testRow)) {
        detail.testRow = detail.testRow.map((row: any) => {
            if (!row || typeof row !== 'object') return row;
            if (!('adjustments' in row)) return row;
            const {adjustments: _adj, ...rest} = row as Record<string, unknown>;
            return rest;
        });
    }
}

/**
 * Keep / rescale costAdj|addAdj|nbvCfAdj|accDepAdj|depnChgAdj with signed line adjustment
 * so AdjustTabs amount edits preserve attr field markers on re-save.
 * Nested row.adjustments are render-only — stripped from the result (not sent back).
 *
 * Shared AdjustTabs / draft path — keeps **top-level** field markers (original shape).
 * If detail still has nested-on-row scalars (PPE fromPaper), hoists them first then
 * runs the original top-level rescale — does not nest outbound.
 *
 * NBV-split preserve is opt-in via {@link PPE_NBV_SPLIT_FLAG} (set only by PPE Cost b/f /
 * Additions companion flow) — default behavior unchanged for all other callers.
 */
export function rescalePpeFieldAdjKeysForAmount(
    detail: Record<string, any> | null | undefined,
    targetAmount: number,
): Record<string, any> {
    if (!detail || typeof detail !== 'object') {
        return {adjustment: targetAmount};
    }
    // Bridge nested fromPaper → top-level so shared rescale logic is unchanged
    const hoisted = hoistNestedPpeFieldAdjScalarsForDraft(detail);
    const next: Record<string, any> = {...hoisted, adjustment: targetAmount};
    const present = PPE_FIELD_ADJ_KEYS.filter((k) => {
        const v = Number(hoisted[k]);
        return Number.isFinite(v) && Math.abs(v) >= 0.001;
    });
    const preserveNbvSplit = hoisted[PPE_NBV_SPLIT_FLAG] === true
        && present.length === 1
        && (present[0] === 'costAdj' || present[0] === 'addAdj');

    if (preserveNbvSplit) {
        // Keep costAdj/addAdj (ΔCost); only line adjustment (ΔNBV) changes
        next[present[0]] = Number(hoisted[present[0]]) || 0;
        next[PPE_NBV_SPLIT_FLAG] = true;
    } else if (present.length === 1) {
        next[present[0]] = targetAmount;
    } else if (present.length > 1) {
        const oldTotal = present.reduce((sum, k) => sum + (Number(hoisted[k]) || 0), 0);
        if (Math.abs(oldTotal) < 0.001) {
            next[present[0]] = targetAmount;
            for (let i = 1; i < present.length; i++) {
                next[present[i]] = 0;
            }
        } else {
            const ratio = targetAmount / oldTotal;
            for (const k of present) {
                next[k] = (Number(hoisted[k]) || 0) * ratio;
            }
        }
    }

    // Render-only nested adjustments — do not keep on payload
    stripPpeRenderAdjustmentsInPlace(next);
    return next;
}

function collectPpeFieldAdjFromDetail(
    detail: Record<string, any>,
    headerId?: string,
    code?: string | null,
): {
    found: boolean;
    total: number;
    fieldSums: Partial<Record<(typeof PPE_FIELD_ADJ_KEYS)[number], number>>;
    ppeRowId?: number;
} {
    let total = 0;
    let found = false;
    let ppeRowId: number | undefined;
    const fieldSums: Partial<Record<(typeof PPE_FIELD_ADJ_KEYS)[number], number>> = {};
    // Legacy full-list sum unless BS/PL code + cross-owned lists (addAdjs+depnChgAdjs) coexist
    const listMap = resolvePpeFieldListMapForCollect(code, detail);

    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = detail[rowKey];
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            const adj = (row as any).adjustments;
            if (!isPpeFieldAdjustmentsObject(adj)) continue;
            for (const {listKey, fieldKey} of listMap) {
                const sum = sumRentalAdjustmentEntriesStrict(
                    (adj as any)[listKey] as AdjustmentEntry[] | null | undefined,
                    headerId,
                );
                if (sum === undefined) continue;
                found = true;
                total += sum;
                fieldSums[fieldKey] = (fieldSums[fieldKey] || 0) + sum;
                const rid = Number((row as any).id);
                if (ppeRowId == null && Number.isFinite(rid) && rid > 0) {
                    ppeRowId = rid;
                }
            }
        }
    }

    return {found, total, fieldSums, ppeRowId};
}

/**
 * PPE WP→WP shape for AdjustTabsDialog:
 * Keep GET xxxRow structure; promote field markers to top-level.
 * Nested/top-level `adjustments` are render-only — stripped after amounts are collected.
 * If detail still has nested-on-row scalars (PPE fromPaper), hoists them first.
 */
export function consolidatePpeWpFieldAdjustments(
    detail: any,
    code?: string,
    headerId?: string,
    lineAdjustmentAmount?: number,
    lineEntry?: string | null,
    originalEntryOverride?: string | null,
): any {
    if (!detail || typeof detail !== 'object') return detail;
    // Only reshape when field-adj evidence exists — leave normal PPE WP details untouched
    if (!isPpeFieldAdjActiveDetail(detail)) return detail;

    // Bridge nested fromPaper → top-level so shared promote logic is unchanged
    const hoisted = hoistNestedPpeFieldAdjScalarsForDraft(detail);
    const collected = collectPpeFieldAdjFromDetail(hoisted, headerId, code);
    // Collect from nested adjustments first, then drop render-only adjustments from payload
    const next: Record<string, any> = {...hoisted};
    stripPpeRenderAdjustmentsInPlace(next);

    const renderEntry = lineEntry === 'Dr' || lineEntry === 'Cr' ? lineEntry : null;
    const existingTop = Number(hoisted.adjustment);
    const lineAbs = Math.abs(Number(lineAdjustmentAmount) || 0);

    let adjustment = 0;
    if (Number.isFinite(existingTop) && Math.abs(existingTop) >= 0.001) {
        if (renderEntry && lineAbs >= 0.001 && Math.abs(Math.abs(existingTop) - lineAbs) < 0.001) {
            adjustment = signLoanAdjustment(renderEntry, lineAbs, code, originalEntryOverride);
        } else if (renderEntry) {
            adjustment = signLoanAdjustment(renderEntry, Math.abs(existingTop), code, originalEntryOverride);
        } else {
            adjustment = existingTop;
        }
    } else if (collected.found && Math.abs(collected.total) >= 0.001) {
        adjustment = renderEntry
            ? signLoanAdjustment(renderEntry, Math.abs(collected.total), code, originalEntryOverride)
            : collected.total;
    } else if (lineAbs >= 0.001) {
        adjustment = signLoanAdjustment(renderEntry ?? 'Dr', lineAbs, code, originalEntryOverride);
    }

    const stripCrossOwned = shouldStripCrossOwnedPpeFieldKeys(code, hoisted);
    const ownedKeys = stripCrossOwned
        ? new Set(ppeFieldListMapForCode(code).map((m) => m.fieldKey))
        : null;

    const hasExistingFieldKey = PPE_FIELD_ADJ_KEYS.some((k) => {
        if (ownedKeys && !ownedKeys.has(k)) return false;
        return Math.abs(Number(hoisted[k]) || 0) >= 0.001;
    });
    if (hasExistingFieldKey) {
        const rescaled = rescalePpeFieldAdjKeysForAmount(
            {
                ...next,
                ...(hoisted.ppeRowId != null && Number(hoisted.ppeRowId) > 0
                    ? {ppeRowId: Number(hoisted.ppeRowId)}
                    : (collected.ppeRowId != null ? {ppeRowId: collected.ppeRowId} : {})),
            },
            adjustment,
        );
        if (ownedKeys) {
            for (const k of PPE_FIELD_ADJ_KEYS) {
                if (!ownedKeys.has(k)) {
                    delete rescaled[k];
                }
            }
        }
        return rescaled;
    }

    next.adjustment = adjustment;
    if (ownedKeys) {
        for (const k of PPE_FIELD_ADJ_KEYS) {
            if (!ownedKeys.has(k)) {
                delete next[k];
            }
        }
    }
    const activeFields = PPE_FIELD_ADJ_KEYS.filter(
        (k) => (!ownedKeys || ownedKeys.has(k))
            && Math.abs(Number(collected.fieldSums[k]) || 0) >= 0.001,
    );
    if (activeFields.length === 1) {
        next[activeFields[0]] = adjustment;
    } else if (activeFields.length > 1) {
        const oldTotal = activeFields.reduce((s, k) => s + (Number(collected.fieldSums[k]) || 0), 0);
        for (const k of activeFields) {
            const part = Number(collected.fieldSums[k]) || 0;
            next[k] = Math.abs(oldTotal) >= 0.001 ? adjustment * (part / oldTotal) : 0;
        }
    }
    if (collected.ppeRowId != null) {
        next.ppeRowId = collected.ppeRowId;
    } else if (hoisted.ppeRowId != null && Number(hoisted.ppeRowId) > 0) {
        next.ppeRowId = Number(hoisted.ppeRowId);
    }
    stripPpeRenderAdjustmentsInPlace(next);
    return next;
}

/**
 * PPE field WP→WP **submit** details (API outbound).
 * Shared draft/AdjustTabs keep top-level markers via {@link rescalePpeFieldAdjKeysForAmount};
 * this helper alone nests field adj onto the target xxxRow and omits empty rows.
 */
export function toPpeFieldAdjSubmitDetail(
    detail: any,
    signedAdjustment: number,
    code?: string | null,
): Record<string, any> {
    const source = detail && typeof detail === 'object' ? detail : {};
    // Capture field markers from source (top-level or nested-on-row) BEFORE rescale
    const sourceNested = collectNestedPpeFieldAdjScalars(source);
    const sourceFieldKeys = PPE_FIELD_ADJ_KEYS.filter((k) => {
        const top = Number(source[k]);
        const nested = Number(sourceNested.fieldSums[k]);
        return (Number.isFinite(top) && Math.abs(top) >= 0.001)
            || (Number.isFinite(nested) && Math.abs(nested) >= 0.001);
    });
    const sourcePpeRowId = Number(source.ppeRowId) > 0
        ? Number(source.ppeRowId)
        : sourceNested.ppeRowId;

    // Shared rescale → top-level markers (incl. hoist nested fromPaper if needed)
    const rescaled = rescalePpeFieldAdjKeysForAmount(source, signedAdjustment);
    const fieldPart: Record<string, any> = {adjustment: signedAdjustment};

    // Strip cross-owned markers only when BS+PL field adjs coexist on the same detail
    const stripCrossOwned = shouldStripCrossOwnedPpeFieldKeys(code, source);
    const ownedKeys = stripCrossOwned
        ? new Set(ppeFieldListMapForCode(code).map((m) => m.fieldKey))
        : null;

    for (const k of PPE_FIELD_ADJ_KEYS) {
        if (ownedKeys && !ownedKeys.has(k)) continue;
        const v = Number(rescaled[k]);
        if (Number.isFinite(v) && Math.abs(v) >= 0.001) {
            fieldPart[k] = v;
        }
    }

    // If rescale wiped markers (e.g. prior nest→normalize with adj=0), restore from source keys
    if (!PPE_FIELD_ADJ_KEYS.some((k) => k in fieldPart) && sourceFieldKeys.length > 0) {
        const keys = ownedKeys
            ? sourceFieldKeys.filter((k) => ownedKeys.has(k))
            : sourceFieldKeys;
        if (keys.length === 1) {
            const k = keys[0];
            const preserveNbv = source[PPE_NBV_SPLIT_FLAG] === true
                && (k === 'costAdj' || k === 'addAdj');
            if (preserveNbv) {
                const keep = Number(source[k] ?? sourceNested.fieldSums[k]) || 0;
                if (Math.abs(keep) >= 0.001) fieldPart[k] = keep;
            } else if (Math.abs(signedAdjustment) >= 0.001) {
                fieldPart[k] = signedAdjustment;
            }
        } else {
            for (const k of keys) {
                const keep = Number(source[k] ?? sourceNested.fieldSums[k]) || 0;
                if (Math.abs(keep) >= 0.001) fieldPart[k] = keep;
            }
        }
    }

    if (!PPE_FIELD_ADJ_KEYS.some((k) => k in fieldPart)) {
        const collected = collectPpeFieldAdjFromDetail(source, undefined, code);
        const active = PPE_FIELD_ADJ_KEYS.filter((k) => Math.abs(Number(collected.fieldSums[k]) || 0) >= 0.001);
        if (active.length === 1 && Math.abs(signedAdjustment) >= 0.001) {
            fieldPart[active[0]] = signedAdjustment;
        } else if (active.length > 1) {
            const oldTotal = active.reduce((s, k) => s + (Number(collected.fieldSums[k]) || 0), 0);
            for (const k of active) {
                const part = Number(collected.fieldSums[k]) || 0;
                fieldPart[k] = Math.abs(oldTotal) >= 0.001 ? signedAdjustment * (part / oldTotal) : 0;
            }
        }
        if (collected.ppeRowId != null && sourcePpeRowId == null) {
            fieldPart.ppeRowId = collected.ppeRowId;
        }
    }

    const ppeRowId = Number(rescaled.ppeRowId) > 0
        ? Number(rescaled.ppeRowId)
        : (sourcePpeRowId != null ? sourcePpeRowId : undefined);
    if (ppeRowId != null && ppeRowId > 0) {
        fieldPart.ppeRowId = ppeRowId;
    }

    // Preserve NBV-split field amounts (ΔCost) when flag set
    const preserveNbvSplit = source[PPE_NBV_SPLIT_FLAG] === true
        || rescaled[PPE_NBV_SPLIT_FLAG] === true;
    if (preserveNbvSplit) {
        for (const k of ['costAdj', 'addAdj'] as const) {
            const keep = Number(
                source[k]
                ?? sourceNested.fieldSums[k]
                ?? rescaled[k],
            );
            if (Math.abs(keep) >= 0.001) fieldPart[k] = keep;
        }
    }

    // Single-field nbvCf/accDep/depnChg: ensure field amount matches line when present
    if (!preserveNbvSplit
        && Math.abs(signedAdjustment) >= 0.001
        && sourceFieldKeys.length === 1
        && !(sourceFieldKeys[0] === 'costAdj' || sourceFieldKeys[0] === 'addAdj')) {
        const k = sourceFieldKeys[0];
        if (!ownedKeys || ownedKeys.has(k)) {
            fieldPart[k] = signedAdjustment;
        }
    }

    const codeUpper = String(code || '').trim().toUpperCase();
    // Only Depn↔NBV row-bound slim (ppeRowId + single nbvCfAdj/depnChgAdj) skips save shell.
    // Empty-category / cost-add / category paths keep prior shell + nest behavior.
    const useSaveShell = !isPpeDepnNbvCrossFieldDetail(source, code)
        && (codeUpper === 'BS-NCA-PPE' || isPpeFieldWpDetail(source));
    const shellSource = useSaveShell
        ? {...toPpeWpSaveShell(source), ...fieldPart}
        : {...fieldPart};

    // Outbound-only: nest onto target row + omit empty xxxRow
    return nestPpeFieldAdjOntoTargetRow(shellSource, {
        ppeRowId: Number(fieldPart.ppeRowId) > 0 ? Number(fieldPart.ppeRowId) : undefined,
        fieldAmounts: Object.fromEntries(
            PPE_FIELD_ADJ_KEYS
                .filter((k) => Math.abs(Number(fieldPart[k]) || 0) >= 0.001)
                .map((k) => [k, Number(fieldPart[k]) || 0]),
        ),
        keepTopLevelAdjustment: false,
    });
}

/**
 * PPE WP shell for shared adjust submit / companion (xxxRow + ppeRate + optional testRow/ppeTitle).
 * Always emits all ten xxxRow keys (empty → []) so AdjustTabs / classify / formulas keep a
 * stable shape. Empty-row omission is only PropertyPlantEquipment Save / PPE field submit
 * ({@link nestPpeFieldAdjOntoTargetRow} / {@link toPpeFieldAdjSubmitDetail}).
 */
export function toPpeWpSaveShell(detail: any): Record<string, any> {
    const source = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : {};
    const next: Record<string, any> = {};

    const stripRowAdj = (row: any) => {
        if (!row || typeof row !== 'object') return row;
        const {adjustments: _adj, ...rest} = row as Record<string, unknown>;
        return {...rest};
    };

    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = source[rowKey];
        next[rowKey] = Array.isArray(rows) ? rows.map(stripRowAdj) : [];
    }

    if (Array.isArray(source.testRow)) {
        next.testRow = source.testRow.map(stripRowAdj);
    }

    if (source.ppeRate && typeof source.ppeRate === 'object' && !Array.isArray(source.ppeRate)) {
        next.ppeRate = {
            ...source.ppeRate,
            equipmentIrdRate: source.ppeRate.equipmentIrdRate ?? 20,
        };
    } else {
        next.ppeRate = {
            locationOfPropertyRate: 4,
            commercialAllowanceRate: 20,
            industrialAllowanceRate: 20,
            furnitureFixtureRate: 20,
            computerHardSoftRate: 20,
            fullPayMotorRate: 30,
            hirePurchaseMotorRate: 30,
            machineryRate: 20,
            liftEscalatorRate: 20,
            equipmentRate: 20,
            equipmentIrdRate: 20,
        };
    }

    if (source.ppeTitle && typeof source.ppeTitle === 'object' && !Array.isArray(source.ppeTitle)) {
        next.ppeTitle = {...source.ppeTitle};
    }

    return next;
}

/**
 * Drop empty PPE Row / testRow arrays from a details payload.
 * Used by PropertyPlantEquipment Save and PPE field WP→WP fromPaper / submit
 * ({@link nestPpeFieldAdjOntoTargetRow} / {@link toPpeFieldAdjSubmitDetail}).
 * Do not use on shared AdjustTabs / classify / GET-shaped helpers that need a
 * stable full shell ({@link toPpeWpSaveShell}).
 */
export function omitEmptyPpeRowKeys<T extends Record<string, any>>(detail: T): T {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return detail;
    const next: Record<string, any> = {...detail};
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = next[rowKey];
        if (Array.isArray(rows) && rows.length === 0) {
            delete next[rowKey];
        }
    }
    if (Array.isArray(next.testRow) && next.testRow.length === 0) {
        delete next.testRow;
    }
    return next as T;
}

/**
 * Flat attr details from line query ({rowClass:"ppeField:costAdj", rowId, adjustment})
 * → PropertyPlantEquipment save-shaped details + top-level field markers for AdjustTabs
 * (never slim-only for BS-NCA-PPE). Nesting is only on API submit via
 * {@link toPpeFieldAdjSubmitDetail}.
 */
export function transformFlatPpeFieldAttrDetails(
    details: any[] | null | undefined,
    lineEntry?: string | null,
    code?: string | null,
): Record<string, any> | null {
    if (!Array.isArray(details) || details.length === 0) return null;
    const first = details[0];
    if (!first || typeof first !== 'object' || !('rowClass' in first)) return null;

    let signedTotal = 0;
    let ppeRowId: number | undefined;
    const fieldSums: Partial<Record<(typeof PPE_FIELD_ADJ_KEYS)[number], number>> = {};

    for (const item of details) {
        if (!item || typeof item !== 'object') continue;
        const rc = String((item as any).rowClass || '').trim();
        const lower = rc.toLowerCase();
        if (!lower.startsWith('ppefield:') && !PPE_FIELD_ADJ_KEYS.some((k) => k.toLowerCase() === lower)) {
            return null;
        }
        const marker = lower.startsWith('ppefield:')
            ? rc.slice(rc.indexOf(':') + 1).trim()
            : rc.trim();
        // Support ppeField:nbvCfAdj:machineryRow (empty-category source row key suffix)
        const fieldMarker = marker.includes(':') ? marker.slice(0, marker.indexOf(':')).trim() : marker;
        const fieldKey = PPE_FIELD_ADJ_KEYS.find((k) => k.toLowerCase() === fieldMarker.toLowerCase());
        if (!fieldKey) continue;
        const amt = Number((item as any).adjustment) || 0;
        if (Math.abs(amt) < 0.001) continue;
        fieldSums[fieldKey] = (fieldSums[fieldKey] || 0) + amt;
        signedTotal += amt;
        const rid = Number((item as any).rowId);
        if (ppeRowId == null && Number.isFinite(rid) && rid > 0) {
            ppeRowId = rid;
        }
    }

    if (Math.abs(signedTotal) < 0.001 && !PPE_FIELD_ADJ_KEYS.some((k) => Math.abs(Number(fieldSums[k]) || 0) >= 0.001)) {
        return null;
    }

    const absTotal = Math.abs(signedTotal);
    const signed = signLoanAdjustment(
        lineEntry === 'Dr' || lineEntry === 'Cr' ? lineEntry : null,
        absTotal >= 0.001 ? absTotal : Math.abs(Object.values(fieldSums).reduce((s, v) => s + (Number(v) || 0), 0)),
        code ?? undefined,
    );
    const fieldPart: Record<string, any> = {adjustment: signed};
    const active = PPE_FIELD_ADJ_KEYS.filter((k) => Math.abs(Number(fieldSums[k]) || 0) >= 0.001);
    if (active.length === 1) {
        fieldPart[active[0]] = signed;
    } else {
        for (const k of active) {
            const part = Number(fieldSums[k]) || 0;
            fieldPart[k] = Math.sign(signed || 1) * Math.abs(part);
        }
    }
    if (ppeRowId != null) fieldPart.ppeRowId = ppeRowId;

    const codeUpper = String(code || '').trim().toUpperCase();
    // Keep prior public hydrate: BS-NCA-PPE never slim-only (empty save shell).
    // Cross-field draft/toPaper builds slim via buildPpeSlimFieldAdjDetail instead.
    if (codeUpper === 'BS-NCA-PPE') {
        return {
            ...toPpeWpSaveShell({}),
            ...fieldPart,
        };
    }
    return fieldPart;
}

function pickLoanAdjListForCode(
    adjustments: Record<string, AdjustmentEntry[] | null | undefined>,
    code?: string,
): AdjustmentEntry[] {
    const upper = String(code || '').trim().toUpperCase();
    if (upper === 'BS-CL-BLS' || upper === 'BS-CL-OFL') {
        return Array.isArray(adjustments.currAdj) ? adjustments.currAdj : [];
    }
    if (upper === 'BS-NCL-BLS' || upper === 'BS-NCL-OFL') {
        return Array.isArray(adjustments.nonCurrAdj) ? adjustments.nonCurrAdj : [];
    }
    if (upper === 'PL-FC-LI') {
        return Array.isArray(adjustments.inAdj) ? adjustments.inAdj : [];
    }
    return [
        ...(Array.isArray(adjustments.currAdj) ? adjustments.currAdj : []),
        ...(Array.isArray(adjustments.nonCurrAdj) ? adjustments.nonCurrAdj : []),
        ...(Array.isArray(adjustments.inAdj) ? adjustments.inAdj : []),
    ];
}

/** Non-DTD: same-side positive / opposite-side negative (matches determineRenderPosition). */
function signLoanAdjustment(
    renderEntry: string | null | undefined,
    absAmount: number,
    code?: string,
    originalEntryOverride?: string | null,
): number {
    if (absAmount < 0.001) return 0;
    const originalEntry = (originalEntryOverride === 'Dr' || originalEntryOverride === 'Cr')
        ? originalEntryOverride
        : (resolveFsNodeOriginalEntry(String(code || ''), originalEntryOverride)
            ?? getOriginalEntryFromFsCategory(String(code || '')));
    const column = renderEntry === 'Cr' ? 'Cr' : 'Dr';
    const sameSide = (originalEntry === 'Dr') === (column === 'Dr');
    return sameSide ? absAmount : -absAmount;
}

/**
 * BankLoan / VehicleLoan WP→WP shape for AdjustTabsDialog:
 * strip nested adjustments {currAdj,nonCurrAdj,inAdj} and expose top-level `adjustment`
 * as a **signed** line amount using non-DTD FS rules (originalEntry + render entry),
 * same contract as commonRow / computeStoredAdjustmentFromUserInput.
 */
export function consolidateLoanWpFieldAdjustments(
    detail: any,
    code?: string,
    headerId?: string,
    lineAdjustmentAmount?: number,
    lineEntry?: string | null,
    originalEntryOverride?: string | null,
): any {
    if (!detail || typeof detail !== 'object') return detail;

    const loanKey = Array.isArray(detail.bankLoans)
        ? 'bankLoans'
        : Array.isArray(detail.vehicleLoans)
            ? 'vehicleLoans'
            : null;
    if (!loanKey) return detail;

    let fieldAdjFromLoans: number | undefined;
    const loans = (detail[loanKey] as any[]).map((loan) => {
        if (!loan || typeof loan !== 'object') return loan;
        const {adjustments, ...rest} = loan as Record<string, any>;
        if (isLoanFieldAdjustmentsObject(adjustments)) {
            const list = pickLoanAdjListForCode(adjustments as any, code);
            const sum = sumAdjustmentEntries(list, headerId);
            if (sum !== undefined) {
                fieldAdjFromLoans = (fieldAdjFromLoans ?? 0) + sum;
            }
        } else if (Array.isArray(adjustments)) {
            const sum = sumAdjustmentEntries(adjustments, headerId);
            if (sum !== undefined) {
                fieldAdjFromLoans = (fieldAdjFromLoans ?? 0) + sum;
            }
        }
        return rest;
    });

    const renderEntry = lineEntry === 'Dr' || lineEntry === 'Cr' ? lineEntry : null;
    const existingTop = Number(detail.adjustment);
    const lineAbs = Math.abs(Number(lineAdjustmentAmount) || 0);
    let adjustment = 0;
    if (Number.isFinite(existingTop) && Math.abs(existingTop) >= 0.001) {
        if (renderEntry && lineAbs >= 0.001 && Math.abs(Math.abs(existingTop) - lineAbs) < 0.001) {
            adjustment = signLoanAdjustment(renderEntry, lineAbs, code, originalEntryOverride);
        } else if (renderEntry) {
            adjustment = signLoanAdjustment(renderEntry, Math.abs(existingTop), code, originalEntryOverride);
        } else {
            adjustment = existingTop;
        }
    } else if (fieldAdjFromLoans !== undefined && Math.abs(fieldAdjFromLoans) >= 0.001) {
        adjustment = renderEntry
            ? signLoanAdjustment(renderEntry, Math.abs(fieldAdjFromLoans), code, originalEntryOverride)
            : fieldAdjFromLoans;
    } else if (lineAbs >= 0.001) {
        adjustment = signLoanAdjustment(renderEntry ?? 'Dr', lineAbs, code, originalEntryOverride);
    }

    return {
        ...detail,
        [loanKey]: loans,
        adjustment,
    };
}

function sumRentalAdjLists(
    adjustments: Record<string, AdjustmentEntry[] | null | undefined> | null | undefined,
    headerId?: string,
): number | undefined {
    if (!adjustments || typeof adjustments !== 'object') return undefined;
    const rental = sumRentalAdjustmentEntriesStrict(adjustments.rentalAdj, headerId);
    const other = sumRentalAdjustmentEntriesStrict(adjustments.otherAdj, headerId);
    if (rental === undefined && other === undefined) return undefined;
    return (rental ?? 0) + (other ?? 0);
}

type RentalAttrAdjItem = AdjustmentEntry & {rowClass?: string | null};

function matchRentalAttrHeader(item: RentalAttrAdjItem, headerId?: string): boolean {
    if (headerId == null) return true;
    const hid = item.adjustHeaderId ?? item.headerId;
    return hid == null || String(hid) === String(headerId);
}

/**
 * From GET `adjustments.{rentalAdj,otherAdj}` (top-level or on rows), rebuild submit maps so
 * backend upserts keep the same marked rowClass (rentalRow:Sep-20 / rentalOtherExpense:id).
 * AdjustTabs must not collapse marked attrs into plain rentalRow.
 */
export function extractRentalAttrSubmitMaps(
    rentalAdj: RentalAttrAdjItem[] | null | undefined,
    otherAdj: RentalAttrAdjItem[] | null | undefined,
    headerId?: string,
): {
    rentalMonthAdjustments: Record<string, number>;
    otherExpenseAdjustmentsById: Record<string, number>;
    rentalAdjustment: number;
    otherExpenseAdjustment: number;
} {
    const rentalMonthAdjustments: Record<string, number> = {};
    const otherExpenseAdjustmentsById: Record<string, number> = {};
    let rentalAdjustment = 0;
    let otherExpenseAdjustment = 0;

    for (const item of rentalAdj || []) {
        if (!item || !matchRentalAttrHeader(item, headerId)) continue;
        const amt = Number(item.adjustment) || 0;
        if (Math.abs(amt) < 0.001) continue;
        const rc = String(item.rowClass || '').trim();
        const idx = rc.indexOf(':');
        const base = (idx > 0 ? rc.slice(0, idx) : rc).trim().toLowerCase();
        const marker = idx > 0 ? rc.slice(idx + 1).trim() : '';
        if ((base === 'rentalrow' || base === 'rows') && marker) {
            rentalMonthAdjustments[marker] = (rentalMonthAdjustments[marker] || 0) + amt;
        } else if (base === 'rentalrow' || base === 'rows' || !rc) {
            rentalAdjustment += amt;
        }
    }
    for (const item of otherAdj || []) {
        if (!item || !matchRentalAttrHeader(item, headerId)) continue;
        const amt = Number(item.adjustment) || 0;
        if (Math.abs(amt) < 0.001) continue;
        const rc = String(item.rowClass || '').trim();
        const idx = rc.indexOf(':');
        const base = (idx > 0 ? rc.slice(0, idx) : rc).trim().toLowerCase();
        const marker = idx > 0 ? rc.slice(idx + 1).trim() : '';
        if (base === 'rentalotherexpense' && marker) {
            otherExpenseAdjustmentsById[marker] = (otherExpenseAdjustmentsById[marker] || 0) + amt;
        } else if (base === 'rentalotherexpense' || !rc) {
            otherExpenseAdjustment += amt;
        }
    }

    return {
        rentalMonthAdjustments,
        otherExpenseAdjustmentsById,
        rentalAdjustment,
        otherExpenseAdjustment,
    };
}

/**
 * Backend GET stamps the same field-adj VO onto every lease row.
 * Dedupe by adjustHeaderId|rowClass so AdjustTabs does not N× sum (e.g. 15980×2).
 */
function rentalAttrDedupeKey(item: RentalAttrAdjItem): string {
    const hid = String(item.adjustHeaderId ?? item.headerId ?? '');
    const rc = String(item.rowClass || '').trim().toLowerCase();
    return `${hid}|${rc}`;
}

function collectRentalAdjListsFromDetail(
    detail: Record<string, any>,
    headerId?: string,
): {rentalAdj: RentalAttrAdjItem[]; otherAdj: RentalAttrAdjItem[]} {
    const rentalAdj: RentalAttrAdjItem[] = [];
    const otherAdj: RentalAttrAdjItem[] = [];
    const seenRental = new Set<string>();
    const seenOther = new Set<string>();

    const take = (adjVo: unknown) => {
        if (!adjVo || typeof adjVo !== 'object' || Array.isArray(adjVo)) return;
        const vo = adjVo as Record<string, unknown>;
        if (Array.isArray(vo.rentalAdj)) {
            for (const item of vo.rentalAdj as RentalAttrAdjItem[]) {
                if (!item) continue;
                const key = rentalAttrDedupeKey(item);
                if (seenRental.has(key)) continue;
                seenRental.add(key);
                rentalAdj.push(item);
            }
        }
        if (Array.isArray(vo.otherAdj)) {
            for (const item of vo.otherAdj as RentalAttrAdjItem[]) {
                if (!item) continue;
                const key = rentalAttrDedupeKey(item);
                if (seenOther.has(key)) continue;
                seenOther.add(key);
                otherAdj.push(item);
            }
        }
    };

    take(detail.adjustments);
    if (Array.isArray(detail.rows)) {
        for (const row of detail.rows) {
            if (row && typeof row === 'object') take((row as any).adjustments);
        }
    }
    // headerId unused here — filtering happens in extractRentalAttrSubmitMaps
    void headerId;
    return {rentalAdj, otherAdj};
}

/**
 * After AdjustTabs amount edit: rescale existing marker maps to new total.
 * Keys (rowClass markers) are never added/removed — only amounts change.
 * PL-AE-REN field WP→WP only — callers must already gate by REN.
 */
export function rescaleRentalFieldAttrMapsForAmount(
    detail: Record<string, any>,
    newTotal: number,
): Record<string, any> {
    if (!detail || typeof detail !== 'object') return detail;

    const monthMap: Record<string, number> = {};
    const otherMap: Record<string, number> = {};
    if (detail.rentalMonthAdjustments && typeof detail.rentalMonthAdjustments === 'object') {
        Object.entries(detail.rentalMonthAdjustments).forEach(([k, v]) => {
            const n = Number(v) || 0;
            if (Math.abs(n) >= 0.001) monthMap[k] = n;
        });
    }
    if (detail.otherExpenseAdjustmentsById && typeof detail.otherExpenseAdjustmentsById === 'object') {
        Object.entries(detail.otherExpenseAdjustmentsById).forEach(([k, v]) => {
            const n = Number(v) || 0;
            if (Math.abs(n) >= 0.001) otherMap[k] = n;
        });
    }
    // Marked maps are the persist source of truth. Aggregates must not double-count the same side
    // (e.g. otherExpenseAdjustment=-30 + otherExpenseAdjustmentsById=-30 with adjustment=-60 →
    // oldSum wrongly -60 / scale 1 / backend writes map-only → attr -30).
    const hasMonthMap = Object.keys(monthMap).length > 0;
    const hasOtherMap = Object.keys(otherMap).length > 0;
    let rentalAgg = hasMonthMap ? 0 : (Number(detail.rentalAdjustment) || 0);
    let otherAgg = hasOtherMap ? 0 : (Number(detail.otherExpenseAdjustment) || 0);

    const oldSum = Object.values(monthMap).reduce((s, n) => s + n, 0)
        + Object.values(otherMap).reduce((s, n) => s + n, 0)
        + rentalAgg
        + otherAgg;

    const next: Record<string, any> = {...detail, adjustment: newTotal};

    if (Math.abs(oldSum) < 0.001) {
        // No preserved attr maps — keep unmarked aggregate only (backend must not invent markers)
        if (Math.abs(newTotal) >= 0.001) {
            next.rentalAdjustment = newTotal;
            next.otherExpenseAdjustment = 0;
        } else {
            delete next.rentalAdjustment;
            delete next.otherExpenseAdjustment;
        }
        delete next.rentalMonthAdjustments;
        delete next.otherExpenseAdjustmentsById;
        return next;
    }

    const scale = newTotal / oldSum;
    const scaledMonth: Record<string, number> = {};
    Object.entries(monthMap).forEach(([k, v]) => {
        const n = v * scale;
        if (Math.abs(n) >= 0.001) scaledMonth[k] = n;
    });
    const scaledOther: Record<string, number> = {};
    Object.entries(otherMap).forEach(([k, v]) => {
        const n = v * scale;
        if (Math.abs(n) >= 0.001) scaledOther[k] = n;
    });
    rentalAgg = Math.abs(rentalAgg) >= 0.001 ? rentalAgg * scale : 0;
    otherAgg = Math.abs(otherAgg) >= 0.001 ? otherAgg * scale : 0;

    if (Object.keys(scaledMonth).length > 0) {
        next.rentalMonthAdjustments = scaledMonth;
        delete next.rentalAdjustment;
    } else {
        delete next.rentalMonthAdjustments;
        if (Math.abs(rentalAgg) >= 0.001) next.rentalAdjustment = rentalAgg;
        else delete next.rentalAdjustment;
    }
    if (Object.keys(scaledOther).length > 0) {
        next.otherExpenseAdjustmentsById = scaledOther;
        delete next.otherExpenseAdjustment;
    } else {
        delete next.otherExpenseAdjustmentsById;
        if (Math.abs(otherAgg) >= 0.001) next.otherExpenseAdjustment = otherAgg;
        else delete next.otherExpenseAdjustment;
    }

    return next;
}

/** True when REN detail has same-side map+aggregate (would double-count) or map/agg sum ≠ target. */
export function rentalFieldAttrMapsOutOfSync(detail: Record<string, any> | null | undefined, targetTotal: number): boolean {
    if (!detail || typeof detail !== 'object') return false;
    const hasMonthMap = detail.rentalMonthAdjustments
        && typeof detail.rentalMonthAdjustments === 'object'
        && Object.keys(detail.rentalMonthAdjustments).length > 0;
    const hasOtherMap = detail.otherExpenseAdjustmentsById
        && typeof detail.otherExpenseAdjustmentsById === 'object'
        && Object.keys(detail.otherExpenseAdjustmentsById).length > 0;
    const rentalAgg = Number(detail.rentalAdjustment) || 0;
    const otherAgg = Number(detail.otherExpenseAdjustment) || 0;
    // Same-side duplicate (the bug that under-writes on backend map-prefer path)
    if (hasMonthMap && Math.abs(rentalAgg) >= 0.001) return true;
    if (hasOtherMap && Math.abs(otherAgg) >= 0.001) return true;

    let sum = 0;
    if (hasMonthMap) {
        Object.values(detail.rentalMonthAdjustments as Record<string, number>).forEach((v) => {
            sum += Number(v) || 0;
        });
    } else {
        sum += rentalAgg;
    }
    if (hasOtherMap) {
        Object.values(detail.otherExpenseAdjustmentsById as Record<string, number>).forEach((v) => {
            sum += Number(v) || 0;
        });
    } else {
        sum += otherAgg;
    }
    if (Math.abs(sum) < 0.001 && Math.abs(targetTotal) < 0.001) return false;
    return Math.abs(sum - targetTotal) >= 0.001;
}

/**
 * PL-AE-REN field WP→WP shape for AdjustTabsDialog:
 * keep `rows` / rentalOtherInfo; expose top-level `adjustment` as a **signed** line amount;
 * preserve attr rowClass via rentalMonthAdjustments / otherExpenseAdjustmentsById maps
 * so AdjustTabs edit/save does not force unmarked rentalRow.
 */
export function consolidateRentalWpFieldAdjustments(
    detail: any,
    code?: string,
    headerId?: string,
    lineAdjustmentAmount?: number,
    lineEntry?: string | null,
    originalEntryOverride?: string | null,
): any {
    if (!detail || typeof detail !== 'object') return detail;
    if (!isRentalFieldWpDetail(detail)) return detail;

    const renderEntry = lineEntry === 'Dr' || lineEntry === 'Cr' ? lineEntry : null;
    const existingTop = Number(detail.adjustment);
    const lineAbs = Math.abs(Number(lineAdjustmentAmount) || 0);
    const {rentalAdj, otherAdj} = collectRentalAdjListsFromDetail(detail, headerId);
    const fieldFromLists = sumRentalAdjLists(
        {rentalAdj, otherAdj},
        headerId,
    );

    // AdjustTabsDialog: PL-AE-REN renders by line.entry + line.adjustmentAmount
    // (Dr → Dr column, Cr → Cr column). Do not prefer summed per-row rentalAdj copies.
    let adjustment = 0;
    if (renderEntry && lineAbs >= 0.001) {
        adjustment = signLoanAdjustment(renderEntry, lineAbs, code, originalEntryOverride);
    } else if (Number.isFinite(existingTop) && Math.abs(existingTop) >= 0.001) {
        if (renderEntry) {
            adjustment = signLoanAdjustment(renderEntry, Math.abs(existingTop), code, originalEntryOverride);
        } else {
            adjustment = existingTop;
        }
    } else if (fieldFromLists !== undefined && Math.abs(fieldFromLists) >= 0.001) {
        adjustment = renderEntry
            ? signLoanAdjustment(renderEntry, Math.abs(fieldFromLists), code, originalEntryOverride)
            : fieldFromLists;
    } else if (lineAbs >= 0.001) {
        adjustment = signLoanAdjustment(renderEntry ?? 'Dr', lineAbs, code, originalEntryOverride);
    }

    const maps = extractRentalAttrSubmitMaps(rentalAdj, otherAdj, headerId);
    // Prefer maps already on detail (e.g. prior edit); else from attr rowClass lists
    const hasExistingMonth = detail.rentalMonthAdjustments
        && typeof detail.rentalMonthAdjustments === 'object'
        && Object.keys(detail.rentalMonthAdjustments).length > 0;
    const hasExistingOther = detail.otherExpenseAdjustmentsById
        && typeof detail.otherExpenseAdjustmentsById === 'object'
        && Object.keys(detail.otherExpenseAdjustmentsById).length > 0;

    const rows = Array.isArray(detail.rows)
        ? detail.rows.map((row: any) => {
            if (!row || typeof row !== 'object') return row;
            const {adjustments: _rowAdj, ...rowRest} = row;
            return rowRest;
        })
        : detail.rows;

    const {adjustments: _adjVo, ...rest} = detail as Record<string, any>;
    const next: Record<string, any> = {
        ...rest,
        rows,
        adjustment,
    };

    const useMonthMap = hasExistingMonth || Object.keys(maps.rentalMonthAdjustments).length > 0;
    const useOtherMap = hasExistingOther || Object.keys(maps.otherExpenseAdjustmentsById).length > 0;

    if (useMonthMap) {
        next.rentalMonthAdjustments = hasExistingMonth
            ? detail.rentalMonthAdjustments
            : maps.rentalMonthAdjustments;
        // Do not also send rentalAdjustment — backend prefers maps and would under-write
    } else if (Number.isFinite(Number(detail.rentalAdjustment)) && Math.abs(Number(detail.rentalAdjustment)) >= 0.001) {
        next.rentalAdjustment = Number(detail.rentalAdjustment);
    } else if (Math.abs(maps.rentalAdjustment) >= 0.001) {
        next.rentalAdjustment = maps.rentalAdjustment;
    }

    if (useOtherMap) {
        next.otherExpenseAdjustmentsById = hasExistingOther
            ? detail.otherExpenseAdjustmentsById
            : maps.otherExpenseAdjustmentsById;
    } else if (Number.isFinite(Number(detail.otherExpenseAdjustment)) && Math.abs(Number(detail.otherExpenseAdjustment)) >= 0.001) {
        next.otherExpenseAdjustment = Number(detail.otherExpenseAdjustment);
    } else if (Math.abs(maps.otherExpenseAdjustment) >= 0.001) {
        next.otherExpenseAdjustment = maps.otherExpenseAdjustment;
    }

    // Align marker/agg totals with line signed adjustment only when out of sync
    // (avoids rewriting healthy drawer payloads that already match)
    if (rentalFieldAttrMapsOutOfSync(next, adjustment)) {
        return rescaleRentalFieldAttrMapsForAmount(next, adjustment);
    }
    next.adjustment = adjustment;
    return next;
}

/** 单行：adjustments/mpfAdjustments → adjustment/mpfAdjustment，保留其余字段 */
export function consolidateWpRowAdjustments(row: any, headerId?: string): any {
    if (!row || typeof row !== 'object') return row;

    const cleaned: Record<string, any> = {...row};

    // Loan field VO object — strip only (amount lives on detail.adjustment after consolidateLoanWpFieldAdjustments)
    if (isLoanFieldAdjustmentsObject(cleaned.adjustments)) {
        delete cleaned.adjustments;
    } else if (isIaFieldAdjustmentsObject(cleaned.adjustments)) {
        // IA nested VO is render-only — amounts live on top-level amorChgAdj/nbvCfAdj after consolidate
        delete cleaned.adjustments;
    } else if (isPpeFieldAdjustmentsObject(cleaned.adjustments)) {
        // PPE nested VO is render-only — amounts live on top-level costAdj/addAdj/… after consolidate
        delete cleaned.adjustments;
    } else if (Array.isArray(cleaned.adjustments)) {
        const fromList = sumAdjustmentEntries(cleaned.adjustments, headerId);
        if (fromList !== undefined) {
            cleaned.adjustment = fromList;
        }
        delete cleaned.adjustments;
    }

    if (Array.isArray(cleaned.mpfAdjustments)) {
        const fromMpfList = sumAdjustmentEntries(cleaned.mpfAdjustments, headerId);
        if (fromMpfList !== undefined) {
            cleaned.mpfAdjustment = fromMpfList;
        }
        delete cleaned.mpfAdjustments;
    }

    if (cleaned.mpf && typeof cleaned.mpf === 'object') {
        cleaned.mpf = consolidateWpRowAdjustments(cleaned.mpf, headerId);
    }

    return cleaned;
}

/** detail 对象：各 row 数组合并 adjustment，保留全部 row；loan WP 再整理为 top-level adjustment */
export function consolidateWpDetailAdjustments(
    detail: any,
    headerId?: string,
    code?: string,
    lineAdjustmentAmount?: number,
    lineEntry?: string | null,
    originalEntryOverride?: string | null,
): any {
    if (!detail || typeof detail !== 'object') return detail;

    const mpfAdjustment = detail.mpfAdjustment;
    if (mpfAdjustment && typeof mpfAdjustment === 'object' && !Array.isArray(mpfAdjustment)) {
        const nextMpfAdjustment: Record<string, any> = {};
        for (const [category, value] of Object.entries(mpfAdjustment)) {
            nextMpfAdjustment[category] = consolidateWpRowAdjustments(value, headerId);
        }
        return {
            ...detail,
            mpfAdjustment: nextMpfAdjustment,
        };
    }

    // IA / ITL field WP→WP only: detect nested adjustments on the ORIGINAL detail
    // before consolidateWpRowAdjustments strips row.adjustments.
    // PL-AE-DEP only when Taxi iaField evidence (not PPE ppeField).
    if (isIaStyleFieldFsCode(code, detail) && isIaFieldAdjActiveDetail(detail)) {
        return consolidateIaWpFieldAdjustments(
            detail,
            code,
            headerId,
            lineAdjustmentAmount,
            lineEntry,
            originalEntryOverride,
        );
    }

    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(detail)) {
        if (!Array.isArray(value)) {
            result[key] = value;
            continue;
        }
        result[key] = value.map((row) => consolidateWpRowAdjustments(row, headerId));
    }

    if (LOAN_DETAIL_ARRAY_KEYS.some((k) => Array.isArray(result[k]))) {
        // Only reshape known loan-field FS codes
        if (isLoanFieldFsCode(code)) {
            return consolidateLoanWpFieldAdjustments(
                result,
                code,
                headerId,
                lineAdjustmentAmount,
                lineEntry,
                originalEntryOverride,
            );
        }
        return result;
    }

    // PL-AE-REN field WP→WP: expose top-level signed adjustment for AdjustTabs `_synthetic`
    if (isRentalFieldFsCode(code) && isRentalFieldWpDetail(result)) {
        return consolidateRentalWpFieldAdjustments(
            result,
            code,
            headerId,
            lineAdjustmentAmount,
            lineEntry,
            originalEntryOverride,
        );
    }

    // PPE field WP→WP only when field-adj keys / nested lists present (not every PPE WP)
    if (isPpeFieldFsCode(code) && isPpeFieldAdjActiveDetail(result)) {
        return consolidatePpeWpFieldAdjustments(
            result,
            code,
            headerId,
            lineAdjustmentAmount,
            lineEntry,
            originalEntryOverride,
        );
    }

    // PL-FC-LI Interest Summary GET only: expose top-level signed adjustment for AdjustTabs `_synthetic`
    if (isInterestSummaryDetail(result, code)) {
        const lineAbs = Math.abs(Number(lineAdjustmentAmount) || 0);
        const existingTop = Number(result.adjustment);
        const absAmount = lineAbs >= 0.001
            ? lineAbs
            : (Number.isFinite(existingTop) ? Math.abs(existingTop) : 0);
        if (absAmount >= 0.001) {
            return {
                ...result,
                adjustment: signLoanAdjustment(
                    lineEntry === 'Dr' || lineEntry === 'Cr' ? lineEntry : null,
                    absAmount,
                    code,
                    originalEntryOverride,
                ),
            };
        }
    }

    return result;
}

/**
 * Depn↔NBV-only header line: consolidate PPE field adj from raw nested lists
 * before row-level strip (shared consolidateWpDetailAdjustments order is unchanged).
 */
function consolidatePpeDepnNbvCrossOnlyLineDetails(
    line: AuditAdjustLine,
    headerId?: string,
): AuditAdjustLine {
    if (!line?.details || !Array.isArray(line.details) || line.details.length === 0) {
        return line;
    }
    const code = String((line as any).code || (line as any).scheduleCode || '').trim();
    if (!isPpeFieldFsCode(code)) {
        return consolidateLineDetails(line, headerId);
    }
    const lineAdj = Number((line as any).adjustmentAmount ?? (line as any).adjustment) || 0;
    const lineEntry = (line as any).entry as string | null | undefined;
    const consolidated = line.details.map((detail) => {
        if (!detail || typeof detail !== 'object') return detail;
        // Flat ppeField:* attrs — leave for loadFromHeader slim / transformFlat
        if ('rowClass' in detail) return detail;
        // Only promote when nested nbvCf/depnChg (or top-level slim markers) exist —
        // do not change costAdj/addAdj-only shells on this opt-in path beyond shared behavior.
        const hasDepnNbvNested = hasNestedPpeDepnNbvAdjustments(detail);
        const hasSlimMarkers = isPpeDepnNbvCrossFieldDetail(detail, code);
        if (!hasDepnNbvNested && !hasSlimMarkers) {
            return consolidateWpDetailAdjustments(
                detail,
                headerId,
                code,
                lineAdj,
                lineEntry,
            );
        }
        return consolidatePpeWpFieldAdjustments(
            detail,
            code,
            headerId,
            lineAdj,
            lineEntry,
            null,
        );
    });
    return {...line, details: consolidated};
}

/**
 * Amor↔NBV-only header line: consolidate IA field adj from raw nested lists
 * before row-level strip (mirrors consolidatePpeDepnNbvCrossOnlyLineDetails).
 */
function consolidateIaAmorNbvCrossOnlyLineDetails(
    line: AuditAdjustLine,
    headerId?: string,
): AuditAdjustLine {
    if (!line?.details || !Array.isArray(line.details) || line.details.length === 0) {
        return line;
    }
    const code = String((line as any).code || (line as any).scheduleCode || '').trim();
    const upper = code.toUpperCase();
    // ITL↔DEP cross header: DEP line uses iaField path; PPE DEP never reaches here
    // (isIaAmorNbvCrossOnlyHeader requires ITL+DEP or IA+AIA).
    if (!isIaFieldFsCode(code) && upper !== PL_AE_DEP_CODE) {
        return consolidateLineDetails(line, headerId);
    }
    const lineAdj = Number((line as any).adjustmentAmount ?? (line as any).adjustment) || 0;
    const lineEntry = (line as any).entry as string | null | undefined;
    const consolidated = line.details.map((detail) => {
        if (!detail || typeof detail !== 'object') return detail;
        // Flat iaField:* attrs — leave for loadFromHeader / toIaFieldSlimDetailFromBackend
        if ('rowClass' in detail) return detail;
        // Only promote when nested amor/nbv lists or slim markers exist (align PPE Depn↔NBV gate)
        const hasNested = hasNestedIaAmorNbvAdjustments(detail);
        const hasSlimMarkers = isIaAmorNbvCrossFieldDetail(detail, code);
        if (!hasNested && !hasSlimMarkers) {
            return consolidateWpDetailAdjustments(
                detail,
                headerId,
                code,
                lineAdj,
                lineEntry,
            );
        }
        return consolidateIaWpFieldAdjustments(
            detail,
            code,
            headerId,
            lineAdj,
            lineEntry,
            null,
        );
    });
    return {...line, details: consolidated};
}

/**
 * When Amor↔NBV pair has one line with slim detail and the other empty,
 * fill the empty side from line.adjustmentAmount + sibling intangibleRowId
 * (PPE Depn↔NBV both sides always carry slim markers after GET).
 */
function fillIaAmorNbvCrossEmptySiblingDetails(
    lines: AuditAdjustLine[],
): AuditAdjustLine[] {
    const iaLine = lines.find((l) => String((l as any)?.code || '').trim().toUpperCase() === 'BS-NCA-IA');
    const aiaLine = lines.find((l) => String((l as any)?.code || '').trim().toUpperCase() === 'PL-AE-AIA');
    if (!iaLine || !aiaLine) return lines;

    const readSlim = (line: AuditAdjustLine) => {
        const d = Array.isArray(line.details) && line.details[0] && typeof line.details[0] === 'object'
            ? line.details[0] as Record<string, any>
            : null;
        if (!d) return null;
        const rowId = Number(d.intangibleRowId) > 0 ? Number(d.intangibleRowId) : undefined;
        return {detail: d, rowId};
    };

    const iaSlim = readSlim(iaLine);
    const aiaSlim = readSlim(aiaLine);
    const sharedRowId = iaSlim?.rowId ?? aiaSlim?.rowId;
    if (!(sharedRowId != null && sharedRowId > 0)) return lines;

    const next = lines.map((line) => {
        const code = String((line as any)?.code || '').trim().toUpperCase();
        const hasDetails = Array.isArray(line.details) && line.details.length > 0;
        if (hasDetails) return line;
        if (code !== 'BS-NCA-IA' && code !== 'PL-AE-AIA') return line;
        const ownedKey = code === 'PL-AE-AIA' ? 'amorChgAdj' : 'nbvCfAdj';
        const lineAdj = Number((line as any).adjustmentAmount ?? (line as any).adjustment) || 0;
        const entry = (line as any).entry === 'Dr' || (line as any).entry === 'Cr'
            ? (line as any).entry
            : null;
        const signed = entry
            ? signLoanAdjustment(entry, Math.abs(lineAdj), code)
            : lineAdj;
        if (Math.abs(signed) < 0.001) return line;
        return {
            ...line,
            details: [buildIaSlimFieldAdjDetail({
                intangibleRowId: sharedRowId,
                fieldKey: ownedKey,
                amount: signed,
            })],
        };
    });
    return next;
}

function consolidateLineDetails(line: AuditAdjustLine, headerId?: string): AuditAdjustLine {
    if (!line?.details || !Array.isArray(line.details) || line.details.length === 0) {
        return line;
    }

    const code = String((line as any).code || (line as any).scheduleCode || '').trim();
    const lineAdj = Number((line as any).adjustmentAmount ?? (line as any).adjustment) || 0;
    const lineEntry = (line as any).entry as string | null | undefined;
    const consolidated = line.details.map((detail) =>
        consolidateWpDetailAdjustments(detail, headerId, code, lineAdj, lineEntry),
    );
    return {...line, details: consolidated};
}

/** getAdjustmentWithWorkingPapers 响应：全 header 合并 adjustments 为 adjustment */
export function consolidateHeaderWpAdjustments(header: AuditAdjustHeader | null | undefined): AuditAdjustHeader | null | undefined {
    if (!header) return header;

    const headerId = header.id;
    // Only Depn↔NBV field pair (BS-NCA-PPE + PL-AE-DEP with nbv/depn evidence):
    // pre-strip PPE field consolidate. All other headers keep shared behavior unchanged.
    const ppeCrossOnly = isPpeDepnNbvCrossOnlyFieldHeader(header.lines);
    // Only Amor↔NBV field pair (BS-NCA-IA + PL-AE-AIA with field evidence)
    const iaCrossOnly = !ppeCrossOnly && isIaAmorNbvCrossOnlyFieldHeader(header.lines);
    let lines = (header.lines || []).map((line) => {
        if (ppeCrossOnly) return consolidatePpeDepnNbvCrossOnlyLineDetails(line, headerId);
        if (iaCrossOnly) return consolidateIaAmorNbvCrossOnlyLineDetails(line, headerId);
        return consolidateLineDetails(line, headerId);
    });
    if (iaCrossOnly) {
        lines = fillIaAmorNbvCrossEmptySiblingDetails(lines);
    }
    if (!ppeCrossOnly && isPpeDepScheduleLevelHeader(header.lines)) {
        lines = slimPpeDepScheduleLineDetails(lines, headerId);
    }
    if (!ppeCrossOnly && !iaCrossOnly && isIaAiaScheduleLevelHeader(header.lines)) {
        lines = slimIaAiaScheduleLineDetails(lines, headerId);
    }
    return {...header, lines};
}

function slimPpeDepScheduleLineDetails(
    lines: AuditAdjustLine[],
    headerId?: string,
): AuditAdjustLine[] {
    let shared: Record<string, any> | null = null;
    const next = lines.map((line) => {
        const code = String((line as any)?.code || (line as any)?.scheduleCode || '').trim().toUpperCase();
        if (code !== 'BS-NCA-PPE' && code !== 'PL-AE-DEP') return line;
        const first = Array.isArray(line.details) ? line.details[0] : null;
        if (!first || typeof first !== 'object') return line;
        const adj = (first as any).adjustments;
        if (!adj || typeof adj !== 'object' || Array.isArray(adj)) return line;
        const matchHeader = (entry: any) => {
            if (headerId == null) return true;
            const hid = entry?.adjustHeaderId ?? entry?.headerId;
            return hid == null || String(hid) === String(headerId);
        };
        const filterList = (raw: unknown) => {
            if (!Array.isArray(raw)) return [];
            const matched = raw.filter(matchHeader);
            return matched.length > 0 ? matched : [];
        };
        const filtered: Record<string, any> = {};
        const depn = filterList(adj.depnChgAdjusts);
        const nbv = filterList(adj.nbvCfAdjusts);
        if (depn.length > 0) filtered.depnChgAdjusts = depn;
        if (nbv.length > 0) filtered.nbvCfAdjusts = nbv;
        if (Object.keys(filtered).length === 0) return {...line, details: []};
        const slim = {adjustments: filtered};
        if (!shared) shared = slim;
        return {...line, details: [slim]};
    });
    if (!shared) return next;
    return next.map((line) => {
        const code = String((line as any)?.code || (line as any)?.scheduleCode || '').trim().toUpperCase();
        if ((code === 'BS-NCA-PPE' || code === 'PL-AE-DEP')
            && (!line.details || !Array.isArray(line.details) || line.details.length === 0)) {
            return {...line, details: [shared!]};
        }
        return line;
    });
}

function slimIaAiaScheduleLineDetails(
    lines: AuditAdjustLine[],
    headerId?: string,
): AuditAdjustLine[] {
    let shared: Record<string, any> | null = null;
    const next = lines.map((line) => {
        const code = String((line as any)?.code || (line as any)?.scheduleCode || '').trim().toUpperCase();
        if (code !== 'BS-NCA-IA' && code !== 'PL-AE-AIA') return line;
        const first = Array.isArray(line.details) ? line.details[0] : null;
        if (!first || typeof first !== 'object') return line;
        const matchHeader = (entry: any) => {
            if (headerId == null) return true;
            const hid = entry?.adjustHeaderId ?? entry?.headerId;
            return hid == null || String(hid) === String(headerId);
        };
        const filterList = (raw: unknown) => {
            if (!Array.isArray(raw)) return [];
            const matched = raw.filter(matchHeader);
            return matched.length > 0 ? matched : [];
        };
        const adj = (first as any).adjustments;
        const adjObj = adj && typeof adj === 'object' && !Array.isArray(adj) ? adj : null;
        const filtered: Record<string, any> = {};
        const amor = filterList(adjObj?.amorChgAdjusts ?? (first as any).amorChgAdjustments ?? (first as any).amorChgAdjusts);
        const nbv = filterList(adjObj?.nbvCfAdjusts ?? (first as any).nbvCfAdjustments ?? (first as any).nbvCfAdjusts);
        if (amor.length > 0) filtered.amorChgAdjusts = amor;
        if (nbv.length > 0) filtered.nbvCfAdjusts = nbv;
        if (Object.keys(filtered).length === 0) return {...line, details: []};
        const slim = {adjustments: filtered};
        if (!shared) shared = slim;
        return {...line, details: [slim]};
    });
    if (!shared) return next;
    return next.map((line) => {
        const code = String((line as any)?.code || (line as any)?.scheduleCode || '').trim().toUpperCase();
        if ((code === 'BS-NCA-IA' || code === 'PL-AE-AIA')
            && (!line.details || !Array.isArray(line.details) || line.details.length === 0)) {
            return {...line, details: [shared!]};
        }
        return line;
    });
}
